import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import TomSelect from "tom-select"
import { renderAssetSelectStep, assetSelectTitle, siteSelectSeedFromPlacement } from "../custom/asset_select_step"
import { renderQrScannerField } from "../custom/qr_scanner_field"
import { reportGroups } from "../custom/wizard_report_preview"

export default class extends Controller {
  static targets = [
    "progressText",
    "stripTitle",
    "stripSummaryLink",
    "stripDots",
    "progressLine",
    "progressStepLabel",
    "progressDoneLabel",
    "progressBarFill",
    "sectionHeader",
    "sectionTitle",
    "sectionDescription",
    "fieldsContainer",
    "errorContainer",
    "errorList",
    "cancelLink",
    "prevBtn",
    "nextBtn",
    "nextBtnLabel",
    "stepRemainingLabel",
    "submitBtn",
    "saveDraftBtn",
    "stepsList",
    "previewNote"
  ]

  // Check glyphs for the sidebar dots and the mobile strip (heroicon "check").
  CHECK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-3.5 w-3.5" aria-hidden="true"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/></svg>'
  CHECK_SVG_SM = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-2.5 w-2.5" aria-hidden="true"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd"/></svg>'

  // Heroicon "list-bullet" (mini) — the pinned Summary entry's dot.
  LIST_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="h-3.5 w-3.5" aria-hidden="true"><path fill-rule="evenodd" d="M6 4.75A.75.75 0 0 1 6.75 4h10.5a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 4.75ZM6 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 10Zm0 5.25a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H6.75a.75.75 0 0 1-.75-.75ZM1.99 4.75a1 1 0 0 1 1-1H3a1 1 0 0 1 1 1v.01a1 1 0 0 1-1 1h-.01a1 1 0 0 1-1-1v-.01ZM1.99 15.25a1 1 0 0 1 1-1H3a1 1 0 0 1 1 1v.01a1 1 0 0 1-1 1h-.01a1 1 0 0 1-1-1v-.01ZM1.99 10a1 1 0 0 1 1-1H3a1 1 0 0 1 1 1v.01a1 1 0 0 1-1 1h-.01a1 1 0 0 1-1-1V10Z" clip-rule="evenodd"/></svg>'

  static values = {
    schema: Object,
    initialData: Object,
    inspectionPhotos: Object,
    submissionId: String,
    saveUrl: String,
    submitUrl: String,
    directUploadUrl: String,
    deletePhotoUrlBase: String,
    // The form builder's Preview (TASK-267.4, decision-76): the same wizard,
    // persisting nothing. Every write this controller can make checks it —
    // autosave (scheduleSave/saveToServer), Submit, photo upload/delete and
    // the QTAG link — and the Review step adds the report table and swaps
    // Submit for Finish Preview. Keep every preview branch behind this one
    // flag; spec/system/form_builder_preview_spec.rb guards that a preview
    // sends no PATCH/POST and creates nothing.
    preview: { type: Boolean, default: false }
  }

  connect() {
    this.currentStep = 0
    // Hub-and-spoke (decision-32), landing amended by TASK-257: a new draft
    // opens on step 1, not the hub — Review & Submit is now reached by
    // finishing the walk, not by default. currentStep remembers the last
    // spoke; onSummary/currentStep are both corrected once below, after the
    // schema and form data are in place, by the same per-step "done" rule
    // the rail uses (stepStatus).
    this.onSummary = false
    // submitAttempted is a per-visit flag (TASK-257): it drives red rail
    // dots / inline Required messages on Review and never persists — a
    // reload always returns to the calm, pre-attempt state.
    this.submitAttempted = false
    this.formData = JSON.parse(JSON.stringify(this.initialDataValue || {}))
    // The customer reference is a column, not part of the jsonb data blob, so
    // it rides alongside formData and is sent top-level on save (the server
    // permits it explicitly).
    this.customerReference = this.schemaValue.header?.customer_reference?.value || ""
    this.fieldErrors = {}
    this.siteFieldErrors = {}
    this.instanceCounts = {}
    this.completedSteps = new Set()
    this.saveTimeout = null
    this.saving = false

    // Build a signed_id -> {id, url} map from server-provided photo records
    this.photoMap = {}
    const photosData = this.inspectionPhotosValue || {}
    Object.values(photosData).flat().forEach(photo => {
      if (photo && photo.signed_id) {
        this.photoMap[photo.signed_id] = { id: photo.id, url: photo.url }
      }
    })

    // The QTag step's pin picker runs inside the shared modal, outside this
    // controller's element, so its answer arrives as an event on the window
    // rather than through a Stimulus action (TASK-177.3).
    this.onFloorplanPinCaptured = event => this.assetPinCaptured(event)
    window.addEventListener("floorplan-pin:captured", this.onFloorplanPinCaptured)

    this.initializeInstanceCounts()
    this.initializeCompletedSteps()

    // Landing (TASK-257): the first step that is not yet "done" by the same
    // rule the rail dots use — which is step 0 for a brand new draft, since
    // nothing is done yet. Every step done lands on Review & Submit instead.
    const total = this.totalSteps
    let landingIndex = -1
    for (let i = 0; i < total; i++) {
      if (this.stepStatus(i) !== "done") { landingIndex = i; break }
    }
    if (landingIndex === -1) {
      this.onSummary = true
    } else {
      this.currentStep = landingIndex
    }

    this.renderStepsSidebar()
    this.renderProgress()
    this.renderCurrentStep()
  }

  disconnect() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    window.removeEventListener("floorplan-pin:captured", this.onFloorplanPinCaptured)
  }

  initializeInstanceCounts() {
    this.schemaValue.sections.forEach(section => {
      if (section.repeatable) {
        const sectionKey = section.section_key
        if (this.formData[sectionKey] && Array.isArray(this.formData[sectionKey])) {
          this.instanceCounts[sectionKey] = this.formData[sectionKey].length
        } else {
          this.instanceCounts[sectionKey] = section.min_repetitions || 1
          this.formData[sectionKey] = []
          for (let i = 0; i < this.instanceCounts[sectionKey]; i++) {
            this.formData[sectionKey].push({ _index: i + 1, _label: "" })
          }
        }
      }
    })
  }

  initializeCompletedSteps() {
    // Mark the QTag step as completed when a saved draft carries an answer the
    // validator would accept — either branch (TASK-177.3).
    if (this.hasAssetSelectStep && this.formData._asset_select && this.assetSelectionSatisfied()) {
      this.completedSteps.add(0)
    }

    // Mark site select step as completed if data exists
    if (this.hasSiteSelectStep && this.formData._site_select) {
      const sd = this.formData._site_select
      if (sd.action === "existing" ? sd.site_id : sd.site?.name) {
        this.completedSteps.add(this.assetSelectOffset)
      }
    }

    this.schemaValue.sections.forEach((section, index) => {
      const stepIndex = index + this.preSectionOffset
      const hasData = section.repeatable
        ? (this.formData[section.section_key] && this.formData[section.section_key].length > 0 &&
           this.formData[section.section_key].some(inst => {
             return section.fields.some(f => {
               const val = inst[f.field_key]
               return val !== undefined && val !== "" && val !== null
             })
           }))
        : section.fields.some(f => {
            const val = this.formData[f.field_key]
            return val !== undefined && val !== "" && val !== null
          })

      if (hasData) this.completedSteps.add(stepIndex)
    })
  }

  // =========================================================================
  // Server persistence
  // =========================================================================

  scheduleSave() {
    if (this.previewValue) return
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.saveToServer(), 2000)
  }

  // Submit-path save: waits out any in-flight autosave first. saveToServer's
  // re-entrancy guard silently drops calls while one is running, so an
  // unflushed submit could validate server-side against stale data — the
  // classic type-then-instantly-submit race.
  async flushSave() {
    while (this.saving) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    await this.saveToServer()
  }

  async saveToServer({ notify = false } = {}) {
    if (this.previewValue) return
    if (this.saving) return
    this.saving = true
    this.setAutosaveStatus("Saving…")

    try {
      const payload = { data: this.formData }
      if (this.schemaValue.header?.customer_reference?.enabled) {
        payload.customer_reference = this.customerReference
      }

      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const json = await response.json()
        if (json.photos) {
          Object.values(json.photos).flat().forEach(photo => {
            if (photo && photo.signed_id) {
              this.photoMap[photo.signed_id] = { id: photo.id, url: photo.url }
              // Patch any rendered delete buttons that still have data-photo-id=""
              this.element.querySelectorAll(`[data-photo-signed-id="${CSS.escape(photo.signed_id)}"]`).forEach(btn => {
                if (!btn.dataset.photoId) btn.dataset.photoId = photo.id
              })
            }
          })
        }
        if (notify) this.dispatchToast("Progress saved", "success")
        this.setAutosaveStatus("All changes saved")
      } else {
        this.dispatchToast("Save failed", "error")
        this.setAutosaveStatus("Save failed — will retry on the next change")
      }
    } catch (e) {
      console.error("Save failed:", e)
      this.dispatchToast("Save failed", "error")
      this.setAutosaveStatus("Save failed — will retry on the next change")
    } finally {
      this.saving = false
    }
  }

  dispatchToast(message, type = "default") {
    window.dispatchEvent(new CustomEvent("toast-show", { detail: { type, message } }))
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  // =========================================================================
  // Navigation state
  // =========================================================================

  get hasSiteSelectStep() {
    return !!this.schemaValue.site_select
  }

  // The QTag step (decision-17): present only when the project opted in —
  // the schema key's absence is what keeps all-off drafts exactly pre-epic.
  get hasAssetSelectStep() {
    return !!this.schemaValue.asset_select
  }

  get assetSelectOffset() {
    return this.hasAssetSelectStep ? 1 : 0
  }

  // The count of pre-section steps — what the single-purpose siteSelectOffset
  // grew into when the QTag step joined (TASK-153.5).
  get preSectionOffset() {
    return this.assetSelectOffset + (this.hasSiteSelectStep ? 1 : 0)
  }

  get isAssetSelectStep() {
    return this.hasAssetSelectStep && this.currentStep === 0
  }

  get isSiteSelectStep() {
    return this.hasSiteSelectStep && this.currentStep === this.assetSelectOffset
  }

  get currentSection() {
    if (this.isAssetSelectStep || this.isSiteSelectStep) return null
    return this.schemaValue.sections[this.currentStep - this.preSectionOffset]
  }

  get totalSteps() {
    return this.schemaValue.sections.length + this.preSectionOffset
  }

  get isLastSectionStep() {
    return this.currentStep === this.totalSteps - 1
  }

  // Titles for every step, in order: the site step (when present) then one
  // per section — the one list the sidebar, the strip and goToStep share.
  stepTitles() {
    const titles = []
    if (this.hasAssetSelectStep) titles.push(assetSelectTitle())
    if (this.hasSiteSelectStep) titles.push("Site Selection")
    ;(this.schemaValue.sections || []).forEach(section => titles.push(section.title))
    return titles
  }

  // Hub-and-spoke (decision-32): the summary hub owns completeness, so every
  // step is free to visit — section rows and issue cards on the hub deep-link
  // anywhere. Gating moved from navigation to Submit (readyToSubmit).
  stepReachable(_index) {
    return true
  }

  goToStep(index) {
    if (!this.onSummary && index === this.currentStep) return

    this.onSummary = false
    this.scheduleSave()
    this.currentStep = index
    this.renderCurrentStep()
  }

  goToSummary() {
    if (this.onSummary) return

    this.onSummary = true
    this.scheduleSave()
    this.renderCurrentStep()
  }

  // ── Sidebar step list (shell, doc §6 W6) ─────────────────────────────────
  // The list lives in the layout's sidebar_left column, outside this
  // controller's element, so it is addressed by id — the same contract the
  // house wizard shell uses, driven from here because steps are client-side.
  // The builder's Preview renders the rail inside the element instead, where
  // it is the stepsList target (it carries no id there).

  get sidebarListElement() {
    if (this.hasStepsListTarget) return this.stepsListTarget
    return document.getElementById("submission-wizard-steps")
  }

  renderStepsSidebar() {
    const list = this.sidebarListElement
    if (!list) return

    list.innerHTML = ""

    this.stepTitles().forEach((title, index) => {
      const item = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "wizard-step-indicator w-full text-left"
      button.dataset.stepIndex = index
      button.addEventListener("click", () => this.goToStep(index))

      const dot = document.createElement("span")
      dot.className = "wizard-step-indicator-dot"
      const label = document.createElement("span")
      label.className = "wizard-step-indicator-title truncate"
      label.textContent = title

      button.append(dot, label)
      item.appendChild(button)
      list.appendChild(item)
    })

    // Review & Submit (decision-32 amended, TASK-257): pinned LAST, below a
    // hairline — the destination the final section's Next reaches, not the
    // flow's landing view.
    const summaryItem = document.createElement("li")
    summaryItem.className = "border-t border-gray-200 mt-2 pt-3"
    const summaryButton = document.createElement("button")
    summaryButton.type = "button"
    summaryButton.className = "wizard-step-indicator w-full text-left is-review"
    summaryButton.dataset.summaryLink = "true"
    summaryButton.addEventListener("click", () => this.goToSummary())
    const summaryDot = document.createElement("span")
    summaryDot.className = "wizard-step-indicator-dot"
    summaryDot.innerHTML = this.CHECK_SVG
    const summaryLabel = document.createElement("span")
    summaryLabel.className = "wizard-step-indicator-title truncate"
    summaryLabel.textContent = "Review & Submit"
    summaryButton.append(summaryDot, summaryLabel)
    summaryItem.appendChild(summaryButton)
    list.appendChild(summaryItem)

    this.updateStepsSidebar()
  }

  updateStepsSidebar() {
    const list = this.sidebarListElement
    if (!list) return

    const summaryButton = list.querySelector("button[data-summary-link]")
    if (summaryButton) {
      summaryButton.classList.toggle("is-current", this.onSummary)
      if (this.onSummary) {
        summaryButton.setAttribute("aria-current", "step")
      } else {
        summaryButton.removeAttribute("aria-current")
      }
    }

    list.querySelectorAll("button[data-step-index]").forEach(button => {
      const index = parseInt(button.dataset.stepIndex, 10)
      const current = !this.onSummary && index === this.currentStep
      const status = this.stepStatus(index)
      const problem = this.submitAttempted && this.stepHasProblem(index)

      button.classList.toggle("is-current", current)
      button.classList.toggle("is-completed", !problem && status === "done")
      button.classList.toggle("is-started", !problem && status === "started")
      button.classList.toggle("is-problem", problem)
      button.disabled = false
      button.classList.remove("cursor-not-allowed")
      if (current) {
        button.setAttribute("aria-current", "step")
      } else {
        button.removeAttribute("aria-current")
      }

      const dot = button.querySelector(".wizard-step-indicator-dot")
      dot.innerHTML = problem ? "!" : (status === "done" ? this.CHECK_SVG : String(index + 1))

      // The small gray "to do" hint on an untouched context step (QTag/Site)
      // — never red, informational only (point 2).
      let hint = button.querySelector("[data-step-hint]")
      const isContextStep = (this.hasAssetSelectStep && index === 0) ||
        (this.hasSiteSelectStep && index === this.assetSelectOffset)
      if (isContextStep && status === "untouched" && !problem) {
        if (!hint) {
          hint = document.createElement("span")
          hint.dataset.stepHint = "true"
          hint.className = "ml-auto text-xs text-gray-400 shrink-0"
          hint.textContent = "To do"
          button.appendChild(hint)
        }
      } else if (hint) {
        hint.remove()
      }
    })
  }

  setAutosaveStatus(text) {
    const el = document.getElementById("submission-wizard-autosave")
    if (el) el.textContent = text
  }

  // ── Mobile progress strip (shell fallback below lg) ──────────────────────

  renderProgress() {
    const titles = this.stepTitles()
    const total = this.totalSteps

    if (this.hasStripTitleTarget) {
      this.stripTitleTarget.textContent = this.onSummary ? "Review & Submit" : (titles[this.currentStep] || "")
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = this.onSummary
        ? `${total} ${total === 1 ? "step" : "steps"}`
        : `Step ${Math.min(this.currentStep + 1, total)} of ${total}`
    }
    if (this.hasStripSummaryLinkTarget) {
      // An inline style, not classList.toggle("hidden", …): this button's
      // own "inline-flex" utility compiles AFTER ".hidden" in the built
      // stylesheet, so the class alone loses the cascade and the link stays
      // visible on Review & Submit — found in the mobile browser pass
      // (TASK-257), fixed at the one call site rather than the build order.
      this.stripSummaryLinkTarget.classList.toggle("hidden", this.onSummary)
      this.stripSummaryLinkTarget.style.display = this.onSummary ? "none" : ""
    }
    if (this.hasStripDotsTarget) {
      this.stripDotsTarget.innerHTML = ""
      titles.forEach((_, index) => {
        const isCurrent = !this.onSummary && index === this.currentStep
        const status = this.stepStatus(index)
        const problem = this.submitAttempted && this.stepHasProblem(index)
        const dot = document.createElement("span")
        dot.className = "wizard-progress-dot" +
          (problem ? " is-problem" : isCurrent ? " is-current" : (status === "done" ? " is-completed" : (status === "started" ? " is-started" : "")))
        dot.innerHTML = problem ? "!" : (status === "done" ? this.CHECK_SVG_SM : String(index + 1))
        this.stripDotsTarget.appendChild(dot)

        if (index < titles.length - 1) {
          const line = document.createElement("span")
          line.className = "flex-1 h-0.5 " + (status === "done" && !problem ? "bg-green-600" : "bg-gray-200")
          this.stripDotsTarget.appendChild(line)
        }
      })

      // The pinned Review & Submit dot (decision-32 amended, TASK-257) —
      // same dashed-ring-check styling as the rail's last item.
      if (titles.length > 0) {
        const line = document.createElement("span")
        line.className = "flex-1 h-0.5 bg-gray-200"
        this.stripDotsTarget.appendChild(line)
      }
      const reviewDot = document.createElement("span")
      reviewDot.className = "wizard-progress-dot is-review" + (this.onSummary ? " is-current" : "")
      reviewDot.innerHTML = this.CHECK_SVG_SM
      this.stripDotsTarget.appendChild(reviewDot)
    }

    // The progress line (board 04) — hidden on Review & Submit, where a
    // "how far through" figure has nothing to measure against.
    if (this.hasProgressLineTarget) {
      this.progressLineTarget.classList.toggle("hidden", this.onSummary)
    }
    if (!this.onSummary) {
      const doneCount = titles.reduce((n, _, index) => n + (this.stepStatus(index) === "done" ? 1 : 0), 0)
      if (this.hasProgressStepLabelTarget) {
        this.progressStepLabelTarget.textContent = `Step ${Math.min(this.currentStep + 1, total)} of ${total}`
      }
      if (this.hasProgressDoneLabelTarget) {
        this.progressDoneLabelTarget.textContent = `${doneCount} of ${total} done`
      }
      if (this.hasProgressBarFillTarget) {
        const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0
        this.progressBarFillTarget.style.width = `${pct}%`
      }
    }
  }


  // =========================================================================
  // Step rendering
  // =========================================================================

  renderCurrentStep() {
    this.hideError()
    if (this.onSummary) {
      this.renderSummaryHub()
    } else {
      this.renderSectionStep()
    }
    this.updateNavigation()
    this.updateStepsSidebar()
    this.renderProgress()
  }

  renderSectionStep() {
    if (this.isAssetSelectStep) {
      this.sectionTitleTarget.textContent = assetSelectTitle()
      this.sectionDescriptionTarget.textContent = ""
      this.sectionHeaderTarget.classList.remove("hidden")
      this.fieldsContainerTarget.classList.remove("hidden")
      this.renderAssetSelectStep()
      // Post-attempt decoration (TASK-257): a still-missing answer is NOT an
      // invalid payload — assetSelectInvalidProblem() must stay false so
      // Next keeps moving on — so this writes straight into the step's own
      // error slot rather than into assetState.error, which that check
      // reads. A real error (foreign tag, failed lookup) already shows
      // there and is left alone.
      if (this.submitAttempted && !this.assetSelectionSatisfied() && !this.assetState?.error) {
        const note = this.fieldsContainerTarget.querySelector("[data-asset-select-error]")
        if (note) {
          note.textContent = this.assetSelectionMessage()
          note.classList.remove("hidden")
        }
      }
      return
    }

    if (this.isSiteSelectStep) {
      this.sectionTitleTarget.textContent = "Site Selection"
      this.sectionDescriptionTarget.textContent = ""
      this.sectionHeaderTarget.classList.remove("hidden")
      this.fieldsContainerTarget.classList.remove("hidden")
      // Post-attempt decoration (TASK-257): populate the same siteFieldErrors
      // the site-step builder's render reads, without calling
      // validateSiteSelectStep itself (owned by that builder, and it
      // re-renders on failure — calling it from here would recurse).
      if (this.submitAttempted) {
        const problems = this.siteSelectProblems()
        if (problems.length > 0) {
          const errs = {}
          problems.forEach(p => { errs[p.fieldKey] = p.message })
          this.siteFieldErrors = errs
        }
      }
      this.renderSiteSelectStep()
      return
    }

    const section = this.currentSection

    this.sectionTitleTarget.textContent = section.title
    this.sectionDescriptionTarget.textContent = section.description || ""
    this.sectionHeaderTarget.classList.remove("hidden")
    this.fieldsContainerTarget.classList.remove("hidden")

    if (section.repeatable) {
      this.renderRepeatableStack(section)
    } else {
      this.renderFields(section.fields, section.section_key)
    }
  }

  // =========================================================================
  // The summary hub (decision-32, TASK-164.3/164.4) — the wizard's landing
  // view: issues, context rows (the pre-section steps), section list with
  // state badges, and the only Submit. Everything is computed client-side
  // from the same rules Next enforces (collectSectionErrors), so the hub and
  // the steps can never disagree.
  // =========================================================================

  renderSummaryHub() {
    this.sectionTitleTarget.textContent = "Review & Submit"
    this.sectionDescriptionTarget.textContent = "Check everything looks right, then submit for review."
    this.sectionHeaderTarget.classList.remove("hidden")
    this.fieldsContainerTarget.classList.remove("hidden")

    const states = this.summarySectionStates()
    const contextRows = this.summaryContextRows()
    const total = this.totalSteps
    const doneCount = states.filter(s => s.state === "complete").length +
      contextRows.filter(r => r.state === "set").length
    const allDone = doneCount === total

    const parts = []
    if (allDone) {
      parts.push(this.reviewReadyCalloutHtml())
    } else if (this.submitAttempted) {
      parts.push(this.reviewAttemptedCalloutHtml(contextRows, states))
    } else {
      parts.push(this.reviewNeutralCalloutHtml(doneCount, total, contextRows, states))
    }

    if (contextRows.length > 0) {
      parts.push('<p class="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Where</p>')
      parts.push(this.summaryContextCardHtml(contextRows))
    }
    parts.push('<p class="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">Sections</p>')
    parts.push(this.summarySectionsCardHtml(states))
    if (this.previewValue) parts.push(this.previewReportCardHtml())

    const container = this.fieldsContainerTarget
    container.innerHTML = `<div class="space-y-3" data-summary-hub>${parts.join("")}</div>`

    container.querySelectorAll("[data-summary-jump]").forEach(el => {
      el.addEventListener("click", event => this.jumpFromSummary(event))
    })
  }

  // The green ready state (board 05/06) — the same text whichever way it is
  // reached: nothing left, pre- or post-attempt.
  reviewReadyCalloutHtml() {
    return `<div class="flex items-start gap-2.5 bg-green-50 border border-green-300 rounded-md p-3">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="h-5 w-5 shrink-0" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#16a34a" stroke-width="1.7"/><path d="M8.5 12.5l2.4 2.4 4.8-5.4" stroke="#16a34a" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <p class="text-sm leading-snug"><strong class="font-semibold text-green-800">Everything's done — ready to submit.</strong></p>
    </div>`
  }

  // Neutral, pre-attempt (board 05): a plain count, never red — Submit is
  // always enabled, this is just where things stand.
  reviewNeutralCalloutHtml(doneCount, total, contextRows, states) {
    const names = []
    contextRows.forEach(r => { if (r.state !== "set") names.push(this.escapeHtml(r.title)) })
    states.forEach(s => { if (s.state !== "complete") names.push(this.escapeHtml(s.section.title)) })
    const tail = names.length > 0
      ? ` ${names.join(", ")} still ${names.length === 1 ? "has" : "have"} something to finish — you can submit when ${names.length === 1 ? "it's" : "they're"} done.`
      : ""
    return `<div class="flex items-start gap-2.5 bg-gray-50 border border-gray-300 rounded-md p-3">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="h-5 w-5 shrink-0" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="#6b7280" stroke-width="1.7"/><path d="M12 11v5m0-8.5h.01" stroke="#6b7280" stroke-width="1.7" stroke-linecap="round"/></svg>
      <p class="text-sm leading-snug text-gray-700"><strong class="font-semibold text-gray-900">${doneCount} of ${total} steps done.</strong>${tail}</p>
    </div>`
  }

  // What exactly is missing on a context step, in words (board 06): the site
  // row names the levels still to choose instead of a blanket "not set".
  contextRowReason(row) {
    if (row.stepIndex === this.assetSelectOffset && this.hasSiteSelectStep) {
      const names = { site: "Site", site_name: "Site name", block: "Block", elevation: "Floor", location: "Location" }
      const missing = [ ...new Set(this.siteSelectionProblems().map(p => names[p.key]).filter(Boolean)) ]
      if (missing.length === 0) return "Not set yet"
      const list = missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`
      return `${list} still to choose`
    }
    if (this.hasAssetSelectStep && row.stepIndex === 0) return "Scan or type the tag's reference"
    return "Not set yet"
  }

  // Attempted, with gaps (board 06): the red callout + the specific list,
  // each row jumping straight to its field.
  reviewAttemptedCalloutHtml(contextRows, states) {
    const items = []
    contextRows.forEach(row => {
      if (row.state === "set") return
      items.push({
        title: row.title, reason: this.contextRowReason(row), where: row.title,
        stepIndex: row.stepIndex, fieldKey: null, instanceIndex: null
      })
    })
    states.forEach(({ section, stepIndex, problems }) => {
      problems.forEach(problem => items.push({
        title: problem.label || problem.fieldKey, reason: problem.message, where: section.title,
        stepIndex, fieldKey: problem.fieldKey, instanceIndex: problem.instanceIndex
      }))
    })

    const rows = items.map(item => {
      const idxAttr = item.instanceIndex === null || item.instanceIndex === undefined ? "" : String(item.instanceIndex)
      return `<div class="flex items-center gap-3 px-3.5 py-3">
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-semibold text-gray-900">${this.escapeHtml(item.title)}</span>
          <span class="block truncate text-[13px] text-red-600">${this.escapeHtml(item.where)} · ${this.escapeHtml(item.reason)}</span>
        </span>
        <button type="button" class="shrink-0 flex items-center gap-0.5 text-sm font-medium text-blue-600"
                data-summary-jump data-step-index="${item.stepIndex}" data-field-key="${this.escapeHtml(item.fieldKey || "")}" data-instance-index="${idxAttr}">
          Go to field
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" class="h-4 w-4" aria-hidden="true"><path d="M7.5 5l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>`
    }).join("")

    return `<div class="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-md p-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="h-5 w-5 shrink-0" aria-hidden="true"><path d="M12 9v4m0 3.5h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" stroke="#dc2626" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <p class="text-sm leading-snug"><strong class="font-semibold text-red-800">${items.length} ${items.length === 1 ? "thing" : "things"} to finish</strong> <span class="text-red-700">before this can be submitted. Nothing has been sent yet.</span></p>
      </div>
      <div class="bg-white border border-gray-300 rounded-lg divide-y divide-gray-100 mt-3">${rows}</div>`
  }

  // Badges read the same before and after an attempt — gray "To do" /
  // green "✓" — and only turn red once submitAttempted, never on their own
  // (board 05 is strictly neutral; board 06 is the same markup with the
  // attempted tone).
  summaryContextCardHtml(contextRows) {
    const rows = contextRows.map(row => {
      const done = row.state === "set"
      const problem = this.submitAttempted && !done
      let badge
      if (done) {
        badge = `<span class="inline-flex items-center gap-1 font-medium rounded border text-xs px-2 py-0.5 bg-green-100 text-green-800 border-green-300">${this.CHECK_SVG_SM}</span>`
      } else {
        const tone = problem ? "bg-red-100 text-red-800 border-red-300" : "bg-gray-100 text-gray-800 border-gray-300"
        badge = `<span class="inline-flex items-center font-medium rounded border text-xs px-2 py-0.5 ${tone}">To do</span>`
      }
      const detailClass = problem ? "text-red-600" : "text-gray-500"
      return `<button type="button" class="w-full flex items-center gap-3 px-3.5 py-2.5 text-left"
        data-summary-jump data-step-index="${row.stepIndex}" data-field-key="" data-instance-index="">
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm text-gray-900">${this.escapeHtml(row.title)}</span>
          <span class="block truncate text-xs ${detailClass}">${this.escapeHtml(row.detail)}</span>
        </span>
        ${badge}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" class="h-4 w-4 shrink-0" aria-hidden="true"><path d="M7.5 5l5 5-5 5" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>`
    }).join("")
    return `<div class="bg-white border border-gray-300 rounded-lg divide-y divide-gray-100">${rows}</div>`
  }

  summarySectionsCardHtml(states) {
    const rows = states.map(({ section, stepIndex, answered, total, state, problems }, i) => {
      const done = state === "complete"
      // Red only for a REAL problem (stepHasProblem's own rule) — an
      // all-optional section sitting at "untouched" has nothing wrong, so
      // it stays the neutral "To do" even once an attempt has run, the same
      // way its rail dot stays gray rather than turning "!".
      const problem = this.submitAttempted && problems.length > 0
      let badge
      if (done) {
        badge = `<span class="inline-flex items-center gap-1 font-medium rounded border text-xs px-2 py-0.5 bg-green-100 text-green-800 border-green-300">${this.CHECK_SVG_SM}${total > 0 ? ` ${answered}/${total}` : ""}</span>`
      } else {
        const label = answered === 0 ? "To do" : `${Math.max(total - answered, 0)} to do`
        const tone = problem ? "bg-red-100 text-red-800 border-red-300" : "bg-gray-100 text-gray-800 border-gray-300"
        badge = `<span class="inline-flex items-center font-medium rounded border text-xs px-2 py-0.5 ${tone}">${label}</span>`
      }
      const repeatSuffix = section.repeatable
        ? ` <span class="text-[13px] text-gray-500">×${this.instanceCounts[section.section_key] || 0}</span>`
        : ""
      return `<button type="button" class="w-full flex items-center gap-3 px-3.5 py-3 text-left"
        data-summary-jump data-step-index="${stepIndex}" data-field-key="" data-instance-index="">
        <span class="min-w-0 flex-1 truncate text-sm text-gray-900"><span class="text-gray-500">${i + 1} ·</span> ${this.escapeHtml(section.title)}${repeatSuffix}</span>
        ${badge}
      </button>`
    }).join("")
    return `<div class="bg-white border border-gray-300 rounded-lg divide-y divide-gray-100">${rows}</div>`
  }

  // Preview only (TASK-267.4, board 06): what the report would print for the
  // answers given so far — label → answer, option LABELS for choices — where
  // a real submission would simply submit. Built by wizard_report_preview.js.
  previewReportCardHtml() {
    const groups = reportGroups({
      sections: this.schemaValue.sections,
      instanceCounts: this.instanceCounts,
      isVisible: (field, sectionKey, index) => this.isFieldVisible(field, sectionKey, index),
      getValue: (fieldKey, sectionKey, index) => this.getFieldValue(fieldKey, sectionKey, index)
    })
    const where = this.hasSiteSelectStep ? this.siteSelectionPathText() : ""
    const row = (label, text, blank) => `<div class="flex items-start gap-4 px-3.5 py-2.5 text-sm">
        <span class="w-2/5 shrink-0 text-gray-500">${this.escapeHtml(label)}</span>
        <span class="min-w-0 flex-1 break-words ${blank ? "text-gray-400" : "text-gray-900"}">${this.escapeHtml(text)}</span>
      </div>`

    const blocks = []
    if (this.hasSiteSelectStep) blocks.push(row("Site & location", where || "—", !where))
    groups.forEach(group => {
      blocks.push(`<div class="px-3.5 pt-3 pb-1 text-xs font-semibold text-gray-700">${this.escapeHtml(group.heading)}</div>`)
      group.rows.forEach(r => blocks.push(row(r.label, r.text, r.blank)))
    })

    return `<p class="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">What the Report Will Print</p>
      <div class="bg-white border border-gray-300 rounded-lg divide-y divide-gray-100" data-preview-report>${blocks.join("")}</div>`
  }

  jumpFromSummary(event) {
    const { stepIndex, fieldKey, instanceIndex } = event.currentTarget.dataset
    const index = parseInt(stepIndex, 10)
    this.goToStep(index)

    requestAnimationFrame(() => {
      // Only decorate (red, inline Required) once an attempt has run — a
      // neutral-state row click just scrolls, it never arms the red state.
      if (this.submitAttempted) this.validateCurrentStep({ silent: true })

      let targetKey = fieldKey || null
      let targetInstance = instanceIndex === "" || instanceIndex === undefined ? null : instanceIndex

      // A section row (no field named) lands on its first unanswered
      // question — a context-step row (site/QTag) has nothing to deep-link
      // to and just lands on the step itself.
      if (!targetKey && !this.isAssetSelectStep && !this.isSiteSelectStep) {
        const found = this.firstUnansweredField(this.currentSection)
        if (found) {
          targetKey = found.fieldKey
          targetInstance = found.instanceIndex
        }
      }
      if (!targetKey) return

      const idxAttr = targetInstance === null || targetInstance === undefined ? "_" : String(targetInstance)
      const wrapper = this.fieldsContainerTarget.querySelector(
        `[data-field-key="${CSS.escape(targetKey)}"][data-instance-index="${idxAttr}"]`
      )
      if (wrapper) {
        wrapper.scrollIntoView({ behavior: "smooth", block: "center" })
        wrapper.querySelector("input, textarea, select, button")?.focus()
      }
    })
  }

  summarySectionStates() {
    return (this.schemaValue.sections || []).map((section, index) => {
      const stepIndex = index + this.preSectionOffset
      const problems = this.collectSectionErrors(section)
      const { answered, total } = this.sectionCounts(section)
      let state
      if (answered === 0 && total > 0) {
        state = "untouched"
      } else if (problems.length > 0) {
        state = "issues"
      } else {
        state = "complete"
      }
      return { section, stepIndex, problems, answered, total, state }
    })
  }

  summaryContextRows() {
    const rows = []

    if (this.hasAssetSelectStep) {
      const config = this.assetSelectConfig
      const answer = this.formData._asset_select
      const required = config.inspect === "required"
      // Green only when the validator would pass it — the badge is the rule,
      // not a count of answered boxes (decision-32 badges, TASK-177.3).
      const satisfied = this.assetSelectionSatisfied()
      let state
      let detail

      if (satisfied && answer) {
        state = "set"
        detail = answer.resolved_reference || answer.reference ||
          (answer.subject === "install" ? "Recorded without a tag" : "Set")
      } else if (required) {
        state = "required"
        detail = answer ? "Incomplete" : "Not scanned yet"
      } else if (answer) {
        state = "required"
        detail = "Incomplete"
      } else {
        state = "unset"
        detail = "Optional — not set"
      }
      rows.push({ title: assetSelectTitle(), stepIndex: 0, state, detail })
    }

    if (this.hasSiteSelectStep) {
      const sd = this.formData._site_select
      const satisfied = this.siteSelectionSatisfied()
      // The answered part of the hierarchy as plain words ("Riverside House ›
      // North Block › Level 1 › Door 1.04") — shown even while a required
      // level is still to choose, so the row says how far the inspector got.
      const path = this.siteSelectionPathText()
      let detail = path || "Not set yet"
      if (!path && satisfied && sd?.action !== "existing") detail = sd?.site?.name || "New site"
      rows.push({ title: "Site & location", stepIndex: this.assetSelectOffset, state: satisfied ? "set" : "required", detail })
    }

    return rows
  }

  sectionCounts(section) {
    const instanceIndices = section.repeatable
      ? Array.from({ length: this.instanceCounts[section.section_key] || 1 }, (_, i) => i)
      : [null]

    let answered = 0
    let total = 0
    instanceIndices.forEach(instanceIndex => {
      section.fields.forEach(field => {
        if (field.field_type === "section_break") return
        if (!this.isFieldVisible(field, section.section_key, instanceIndex)) return
        total++
        const value = this.getFieldValue(field.field_key, section.section_key, instanceIndex)
        const blank = value === "" || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0)
        if (!blank) answered++
      })
    })
    return { answered, total }
  }

  // THE SAME RULES validateSiteSelectStep APPLIES, silently. The rail/hub
  // badge used to be satisfied by a site alone, so a REQUIRED floor or
  // location left blank still showed green — this is that bug's fix: a
  // single rule source (siteSelectionProblems) that both the badge and the
  // Next-button validator read, so they can never disagree again.
  //
  // Customer reference is deliberately NOT one of these problems: it shares
  // this step's screen but is a spine field, not part of "Site & location".
  siteSelectionProblems() {
    if (!this.hasSiteSelectStep) return []

    const siteData = this.formData._site_select
    const config = this.schemaValue.site_select?.config || {}
    const data = (siteData && typeof siteData === "object") ? siteData : {}
    const problems = []
    const add = (key, message, blank) => problems.push({ key, message, blank })

    if (data.action === "new") {
      if (!data.site?.name || data.site.name.trim() === "") {
        add("site_name", "Site name is required", true)
      }
    } else {
      if (!data.site_id || data.site_id.toString().trim() === "") {
        add("site", "A site must be selected", true)
      }
    }

    const showBlockV     = config.show_block !== false
    const showElevationV = showBlockV && config.show_elevation !== false
    const showLocationV  = config.show_location !== false

    if (showBlockV && config.require_block === true && !this.hierarchyLevelAnswered("block")) {
      add("block", "Block is required", true)
    }

    if (showElevationV) {
      // Tagged form (decision-4): a selected floor is { id: ... } — its level
      // already exists server-side and needs no numeric check. A typed new
      // floor is { level: "..." } and does.
      const stored = data.elevation
      const isSelection = !!(stored && typeof stored === "object" && stored.id)
      const typedLevel = (stored && typeof stored === "object") ? stored.level : stored

      if (stored === undefined || stored === null || stored === "") {
        if (config.require_elevation !== false) add("elevation", "Floor is required", true)
      } else if (!isSelection) {
        const raw = String(typedLevel ?? "").trim()
        if (!/^-?\d+$/.test(raw)) {
          // Whole numbers only — Elevation#level is an integer column, so a
          // decimal used to pass here and then fail at save, after the
          // inspector had filled in the entire wizard. NOT a blank: there is
          // a value, it's just malformed, so Next must still block on it.
          add("elevation", "Floor must be a whole number", false)
        } else {
          const level = parseInt(raw, 10)
          if (level < -5 || level > 50) add("elevation", "Floor must be between -5 and 50", false)
        }
      }
    }

    if (showLocationV && config.require_location !== false) {
      if (!data.location || data.location.toString().trim() === "") {
        add("location", "Location is required", true)
      }
    }

    return problems
  }

  siteSelectionSatisfied() {
    if (!this.hasSiteSelectStep) return true
    return this.siteSelectionProblems().length === 0
  }

  // A is gating Next around this method (decision amendment: Next never
  // blocks on a missing required answer, only on a malformed one). True
  // when every current problem is a blank — nothing is actually invalid,
  // just unanswered — so Next may proceed and the step shows Required
  // messages instead of stopping navigation.
  siteSelectBlankOnly() {
    const problems = this.siteSelectionProblems()
    return problems.length > 0 && problems.every(problem => problem.blank)
  }

  // THE SAME RULES THE SERVER RUNS (InspectionValidator#validate_asset_selection,
  // TASK-177.3). The hub's badge is this predicate, not an answered-count: a
  // step that says green and then collects a 422 is worse than one that says
  // red, and the two must agree about which branches count.
  //
  // Read in the retired vocabulary the resolved config still emits
  // (decision-41's compatibility spine): inspect === "required" IS qtag_step
  // required, install_tagging === "required" IS the tag mandate that follows
  // from it.
  assetSelectionSatisfied() {
    if (!this.hasAssetSelectStep) return true

    const config = this.assetSelectConfig
    const answer = this.formData._asset_select
    const required = config.inspect === "required"

    if (!answer) return !required

    if (answer.subject === "install") {
      if (!answer.product_type_id) return false
      // Required IS the tag mandate: an untagged record is refused, here and
      // at the server, rather than discovered at submit.
      if (config.install_tagging === "required" && !answer.reference) return false
      return true
    }

    if (answer.subject === "inspect") return !!(answer.resolved_reference || answer.reference)

    return !required
  }

  readyToSubmit() {
    const sectionsClean = (this.schemaValue.sections || [])
      .every(section => this.collectSectionErrors(section).length === 0)
    return sectionsClean && this.siteSelectionSatisfied() && this.assetSelectionSatisfied()
  }

  // =========================================================================
  // Guided flow (TASK-257): per-step status for the rail, the mobile strip,
  // the progress line and Review & Submit — all four read this one function
  // so they can never disagree about which steps are done.
  //
  //   untouched — nothing answered yet
  //   started   — something answered/scanned, but would not yet pass Submit
  //   done      — passes the same checks Submit runs (collectSectionErrors /
  //               siteSelectionSatisfied / assetSelectionSatisfied)
  //
  // Red is not a fourth status here — it is a submitAttempted-only overlay
  // the rail/strip/Review apply on top of "started" or "untouched"
  // (stepHasProblem), never stored as state of its own.
  // =========================================================================

  stepStatus(index) {
    if (this.hasAssetSelectStep && index === 0) {
      const answer = this.formData._asset_select
      const touched = !!answer || !!(this.assetState && this.assetState.reference)
      if (this.assetSelectionSatisfied() && answer) return "done"
      return touched ? "started" : "untouched"
    }

    if (this.hasSiteSelectStep && index === this.assetSelectOffset) {
      const sd = this.formData._site_select
      const touched = !!(sd && (sd.site_id || sd.site?.name || sd.action))
      if (this.siteSelectionSatisfied()) return "done"
      return touched ? "started" : "untouched"
    }

    const section = this.schemaValue.sections[index - this.preSectionOffset]
    if (!section) return "untouched"

    const { answered, total } = this.sectionCounts(section)
    if (answered === 0) return total > 0 ? "untouched" : "done"
    return this.collectSectionErrors(section).length === 0 ? "done" : "started"
  }

  // Whether Review's attempted list / the rail's red dot applies to this
  // step. Only meaningful once submitAttempted is true — callers gate on
  // that themselves, the same way stepHasProblem never checks it itself.
  stepHasProblem(index) {
    if (this.hasAssetSelectStep && index === 0) return !this.assetSelectionSatisfied()
    if (this.hasSiteSelectStep && index === this.assetSelectOffset) return !this.siteSelectionSatisfied()

    const section = this.schemaValue.sections[index - this.preSectionOffset]
    if (!section) return false
    return this.collectSectionErrors(section).length > 0
  }

  // The footer's "<n> still to answer here" — a plain blank count, not a
  // blocking gate (Next only blocks on the invalid-value split below).
  remainingCount(index) {
    if (this.hasAssetSelectStep && index === 0) return this.assetSelectionSatisfied() ? 0 : 1
    if (this.hasSiteSelectStep && index === this.assetSelectOffset) return this.siteSelectionSatisfied() ? 0 : 1

    const section = this.schemaValue.sections[index - this.preSectionOffset]
    if (!section) return 0
    const { answered, total } = this.sectionCounts(section)
    return Math.max(total - answered, 0)
  }

  // The first blank field in a section, in schema order — what a Review row
  // click with no specific field (a section row, not an issue row) scrolls
  // to. Repeatable sections are walked instance-by-instance.
  firstUnansweredField(section) {
    if (!section) return null
    const instanceIndices = section.repeatable
      ? Array.from({ length: this.instanceCounts[section.section_key] || 1 }, (_, i) => i)
      : [null]

    for (const instanceIndex of instanceIndices) {
      for (const field of section.fields) {
        if (field.field_type === "section_break") continue
        if (!this.isFieldVisible(field, section.section_key, instanceIndex)) continue
        const value = this.getFieldValue(field.field_key, section.section_key, instanceIndex)
        const blank = value === "" || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0)
        if (blank) return { fieldKey: field.field_key, instanceIndex }
      }
    }
    return null
  }

  // THE ONE INVALID-VALUE CHECK on the asset step (TASK-257): an active
  // error on screen (a foreign tag, a failed lookup/add) is a genuine block;
  // an absent answer — nothing scanned yet, required or not — is merely
  // unfinished and must not stop Next (decision-32 amended).
  assetSelectInvalidProblem() {
    return !!(this.assetState && this.assetState.error)
  }

  // THE ONE INVALID-VALUE CHECK on the site step (TASK-257): a typed floor
  // that isn't a whole number in range. Every other check in
  // validateSiteSelectStep (owned by the site-step builder, untouched here)
  // is a blank/required check and must not block Next either — read-only
  // mirror of that method's elevation branch, kept separate so Next can gate
  // on it without triggering that method's own decorate-and-toast.
  siteSelectProblems() {
    if (!this.hasSiteSelectStep) return []
    const data = this.siteSelectData()
    const config = this.schemaValue.site_select?.config || {}
    const problems = []
    const add = (fieldKey, message, kind) => problems.push({ fieldKey, message, kind })

    const refCfg = this.schemaValue.header?.customer_reference || {}
    if (refCfg.enabled && refCfg.required && !(this.customerReference || "").trim()) {
      add("customer_reference", `${refCfg.label || "Your Ref"} is required`, "blank")
    }

    if (data.action === "new") {
      if (!data.site?.name || data.site.name.trim() === "") add("site_name", "Site name is required", "blank")
    } else if (!data.site_id || data.site_id.toString().trim() === "") {
      add("site", "A site must be selected", "blank")
    }

    const showBlockV = config.show_block !== false
    const showElevationV = showBlockV && config.show_elevation !== false
    const showLocationV = config.show_location !== false

    if (showBlockV && config.require_block === true && !this.hierarchyLevelAnswered("block")) {
      add("block", "Block is required", "blank")
    }

    if (showElevationV) {
      const stored = data.elevation
      const isSelection = !!(stored && typeof stored === "object" && stored.id)
      const typedLevel = (stored && typeof stored === "object") ? stored.level : stored

      if (stored === undefined || stored === null || stored === "") {
        if (config.require_elevation !== false) add("elevation", "Floor is required", "blank")
      } else if (!isSelection) {
        const raw = String(typedLevel ?? "").trim()
        if (!/^-?\d+$/.test(raw)) {
          add("elevation", "Floor must be a whole number", "invalid")
        } else {
          const level = parseInt(raw, 10)
          if (level < -5 || level > 50) add("elevation", "Floor must be between -5 and 50", "invalid")
        }
      }
    }

    if (showLocationV && config.require_location !== false) {
      if (!data.location || data.location.toString().trim() === "") add("location", "Location is required", "blank")
    }

    return problems
  }

  siteSelectInvalidProblem() {
    return this.siteSelectProblems().some(p => p.kind === "invalid")
  }

  // =========================================================================
  // The QTag step (TASK-153.5, redrawn scan-first by TASK-177.3,
  // decision-41) — drawn by the shared module, wired here.
  //
  // ONE SCAN BOX, NO MODE TABS. The tag decides the branch, and the SERVER
  // decides which tag it is: the lookup answers `capture.branch`, which is the
  // same question the submit re-asks (Qtags::CaptureBranch mirrors
  // resolve_subject_reference / resolve_fresh_tag). The step's in-memory state
  // lives on this.assetState; the durable answer is formData._asset_select.
  //
  //   linked tag       → subject "inspect", the resolved_reference is the
  //                      SERVER's (written back on lookup, re-verified at
  //                      submit)
  //   our unused tag   → subject "install" + reference + product_type_id: the
  //                      existing instance-creating path, which is why the
  //                      payload contract barely moved
  //   no tag at all    → subject "install" + product_type_id, offered only
  //                      when the step is optional (Required IS the mandate)
  //   somebody else's  → no answer written at all; add-to-my-sites first
  // =========================================================================

  renderAssetSelectStep() {
    const container = this.fieldsContainerTarget
    container.innerHTML = ""

    if (!this.assetState) {
      this.assetState = this.assetStateFromDraft()
      // A saved draft carries the answer but not the payload; re-look it up so
      // the card can be drawn, rather than trusting what the draft remembers.
      //
      // ARMED HERE, ONCE, AND NEVER BY A RENDER. Asking the rendered state
      // instead — "entry phase with a reference" — made the lookup a side
      // effect of drawing the step, and every error branch leaves exactly that
      // state behind: the error re-rendered, the render re-looked-up, and one
      // click on a reference that does not resolve hammered the endpoint until
      // the page was navigated away from (TASK-177.5 QA). A lookup now happens
      // only on this one draft resume or on the button / Enter.
      this.assetDraftLookupPending =
        this.assetState.phase === "entry" && !!this.assetState.reference
    }

    // The pin block needs the site's tree to know whether the chosen floor has
    // a plan, and this step comes BEFORE the Where step that would have
    // fetched it. Kick it off once; the step redraws when it lands.
    this.ensureHierarchyForAssetPin()

    renderAssetSelectStep(container, { config: this.assetSelectConfig, state: this.assetStateForRender })
    this.bindAssetSelectEvents(container)

    if (this.assetDraftLookupPending) {
      this.assetDraftLookupPending = false
      this.assetLookup()
    }
  }

  // What a resumed draft means, before the server has re-answered. A tagged
  // answer re-looks itself up; an untagged record has nothing to look up and
  // opens on its own pane.
  assetStateFromDraft() {
    const answer = this.formData._asset_select
    if (!answer) return { phase: "entry", reference: "" }

    if (answer.reference) {
      return { phase: "entry", reference: answer.reference, productTypeId: answer.product_type_id || "" }
    }
    if (answer.subject === "install") {
      return { phase: "untagged", reference: "", productTypeId: answer.product_type_id || "" }
    }
    return { phase: "entry", reference: "" }
  }

  // The product list and the pin are schema and payload, not step memory —
  // merged in at render so a reset or a re-lookup can never drop them.
  get assetStateForRender() {
    return {
      ...(this.assetState || {}),
      products: this.schemaValue.asset_select?.products || [],
      pin: this.assetPinForDisplay,
      pinContext: this.assetPinContext
    }
  }

  get assetSelectConfig() {
    return this.schemaValue.asset_select?.config || {}
  }

  // THE PIN, THREE WAYS (decision-41, mirroring the materialiser). The key's
  // ABSENCE means "leave whatever pin exists alone", so the answer is asked
  // whether it holds the key at all rather than whether the value is truthy —
  // reading absence as "no pin" is exactly how a re-inspection would wipe a
  // placement somebody made last year.
  get assetPinForDisplay() {
    const answer = this.formData._asset_select
    if (answer && Object.prototype.hasOwnProperty.call(answer, "floorplan_pin")) {
      return answer.floorplan_pin
    }
    return this.assetState?.serverPin || null
  }

  // Where the picker would open, and whether it can open at all. The Where
  // step is answered AFTER this one, so "not yet" is an ordinary state.
  get assetPinContext() {
    const elevationId = this.selectedHierarchyId("elevation")
    if (!elevationId) return { available: false }

    const elevation = this.hierarchyOptionsFor("elevation").find(e => e.id === elevationId)
    if (!elevation || !elevation.has_floorplan) return { available: false }

    const blockId = this.selectedHierarchyId("block")
    const siteId = this.siteSelectData().site_id
    if (!blockId || !siteId) return { available: false }

    // `capture=1` is the picker's no-write variant: it hands coordinates back
    // as an event instead of POSTing them, because on the fresh-tag branch the
    // ProductInstance they belong to does not exist until submit.
    const params = new URLSearchParams({ capture: "1" })
    const pin = this.assetPinForDisplay
    if (pin) {
      params.set("x_percent", pin.x_percent)
      params.set("y_percent", pin.y_percent)
      params.set("page_number", pin.page_number)
    }

    return {
      available: true,
      elevationLabel: `Level ${elevation.level}`,
      elevationLevel: elevation.level,
      url: `/sites/${siteId}/blocks/${blockId}/elevations/${elevationId}/floorplan-pins/new?${params.toString()}`
    }
  }

  ensureHierarchyForAssetPin() {
    const siteId = this.siteSelectData().site_id
    if (!siteId || this.siteHierarchy || this.hierarchyLoading) return
    // One attempt per site: a failed fetch must not turn every redraw into
    // another request.
    if (this.assetHierarchyRequestedFor === siteId) return

    this.assetHierarchyRequestedFor = siteId
    this.loadHierarchy(siteId).then(() => {
      if (!this.onSummary && this.isAssetSelectStep) this.renderCurrentStep()
    })
  }

  bindAssetSelectEvents(container) {
    container.querySelector("[data-asset-select-product]")?.addEventListener("change", event => {
      this.assetProductChoice(event.target.value)
    })
    container.querySelector("[data-asset-select-lookup]")?.addEventListener("click", () => this.assetLookup())
    container.querySelector("[data-asset-select-add]")?.addEventListener("click", () => this.assetAdd())
    container.querySelector("[data-asset-select-reset]")?.addEventListener("click", () => this.assetReset())
    container.querySelector("[data-asset-select-untag]")?.addEventListener("click", () => this.assetUntagged())
    container.querySelector("[data-asset-select-pin-remove]")?.addEventListener("click", () => this.assetPinRemove())
    container.querySelector("[data-asset-select-input]")?.addEventListener("keydown", event => {
      if (event.key === "Enter") {
        event.preventDefault()
        this.assetLookup()
      }
    })
  }

  async assetLookup() {
    const input = this.fieldsContainerTarget.querySelector("[data-asset-select-input]")
    const reference = (input?.value || this.assetState?.reference || "").trim()
    if (!reference) return this.assetError("Type or scan a tag reference first.")

    try {
      const response = await fetch(`/qtag_lookup?reference=${encodeURIComponent(reference)}`, {
        headers: { "Accept": "application/json" }
      })
      const payload = await response.json()

      if (response.status === 404) return this.assetError("No tag with that reference.")
      if (response.status === 422) return this.assetError("That's not a QTAG reference.")
      if (response.status === 429) return this.assetError("Too many lookups — give it a moment.")
      if (!response.ok) return this.assetError("The lookup failed — try again.")

      this.applyAssetPayload(reference, payload)
    } catch {
      this.assetError("No connection — try again when you're back online.")
    }
  }

  async assetAdd() {
    const reference = this.assetState?.reference
    if (!reference) return
    // Linking a tag writes to the team's estate — never from a preview.
    if (this.previewValue) return this.assetError("Adding a tag isn't available in a preview.")

    try {
      const response = await fetch("/qtag_lookup/link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ reference })
      })
      const payload = await response.json()

      if (!response.ok) {
        return this.assetError(payload?.error?.message || "That tag can't be added right now.")
      }

      this.applyAssetPayload(reference, payload)
    } catch {
      this.assetError("No connection — try again when you're back online.")
    }
  }

  // The server's branch, never the client's guess. `capture.branch` comes
  // from Qtags::CaptureBranch, which asks the questions the submit will ask.
  applyAssetPayload(reference, payload) {
    const branch = payload.capture?.branch
    const resolved = payload.qtag?.reference || reference
    // A re-lookup (resumed draft, or the add-to-my-sites round trip) must not
    // lose a product the engineer already chose.
    const productTypeId = this.assetState?.productTypeId || ""

    if (branch === "linked") {
      this.assetState = {
        phase: "resolved", reference, payload, productTypeId,
        // Where the asset is ALREADY pinned, for display. Not written into the
        // answer: an untouched pin must stay untouched, which is what the
        // absent key means to the materialiser.
        serverPin: payload.capture?.pin || null
      }
      this.assetAnswer({ subject: "inspect", reference, resolved_reference: payload.qtag?.reference })

      // Seed site-select from the placement when nothing chose a site yet —
      // the human at the door still outranks the record on its own step.
      const placement = payload.linked?.placement
      if (placement && !this.formData._site_select?.site_id && !this.formData._site_select?.site?.name) {
        const seed = siteSelectSeedFromPlacement(placement)
        if (seed) this.formData._site_select = seed
      }

      this.completedSteps.add(0)
      this.scheduleSave()
      this.renderCurrentStep()
      return
    }

    if (branch === "fresh") {
      // Our own sticker, on nothing yet: record what it is going on. The
      // answer is the install path's — same shape the wizard has always
      // written for a tagged install.
      this.assetState = { phase: "fresh", reference: resolved, payload, productTypeId }
      this.assetAnswer(productTypeId
        ? { subject: "install", reference: resolved, product_type_id: productTypeId }
        : null)
      this.completedSteps.delete(0)
      this.scheduleSave()
      this.renderCurrentStep()
      return
    }

    if (branch === "foreign") {
      // Resolvable but not ours: offer the add, and do not write an answer —
      // an unheld tag is not a subject the team can file against.
      this.assetState = { phase: "foreign", reference, payload }
      this.assetAnswer(null)
      this.completedSteps.delete(0)
      this.renderCurrentStep()
      return
    }

    // Another team's tag that is on nothing: there is no record to adopt and
    // it is not ours to fit something to. Say so, rather than offering an add
    // the server will refuse.
    this.assetAnswer(null)
    this.completedSteps.delete(0)
    this.assetState = { phase: "entry", reference }
    this.assetError(`QTAG ${resolved} belongs to another team and isn't on anything yet.`)
  }

  // Writes the step's durable answer, PRESERVING the pin key when one is
  // already on it — the pin outlives a re-lookup or a product change.
  assetAnswer(answer) {
    const previous = this.formData._asset_select
    const hadPin = previous && Object.prototype.hasOwnProperty.call(previous, "floorplan_pin")

    if (!answer) {
      delete this.formData._asset_select
      return
    }

    if (hadPin) answer.floorplan_pin = previous.floorplan_pin
    this.formData._asset_select = answer
  }

  assetReset() {
    this.assetState = { phase: "entry", reference: "" }
    delete this.formData._asset_select
    this.completedSteps.delete(0)
    this.scheduleSave()
    this.renderCurrentStep()
  }

  assetError(message) {
    this.assetState = { ...(this.assetState || { phase: "entry" }), error: message }
    const input = this.fieldsContainerTarget.querySelector("[data-asset-select-input]")
    if (input) this.assetState.reference = input.value.trim()
    this.renderCurrentStep()
  }

  // The one escape from the scan box (decision-41), offered only when the step
  // is optional: an asset recorded with no tag on it at all.
  assetUntagged() {
    this.assetState = { phase: "untagged", reference: "", productTypeId: "" }
    delete this.formData._asset_select
    this.completedSteps.delete(0)
    this.renderCurrentStep()
  }

  assetProductChoice(productTypeId) {
    const phase = this.assetState?.phase
    if (phase !== "fresh" && phase !== "untagged") return

    this.assetState = { ...this.assetState, productTypeId, error: null }

    if (!productTypeId) {
      this.assetAnswer(null)
      this.completedSteps.delete(0)
      return
    }

    const answer = { subject: "install", product_type_id: productTypeId }
    if (phase === "fresh") answer.reference = this.assetState.reference
    this.assetAnswer(answer)
    this.completedSteps.add(0)
    this.scheduleSave()
  }

  // ── The pin ──────────────────────────────────────────────────────────────
  // Captured by the picker's no-write variant and handed over as an event,
  // because on the fresh-tag branch there is no ProductInstance to POST
  // against yet. It rides out on the answer and is materialised at submit.

  assetPinCaptured(event) {
    if (!this.hasAssetSelectStep) return
    const answer = this.formData._asset_select
    if (!answer) return

    const { page_number, x_percent, y_percent } = event.detail || {}
    answer.floorplan_pin = { page_number, x_percent, y_percent }
    this.scheduleSave()
    if (!this.onSummary && this.isAssetSelectStep) this.renderCurrentStep()
  }

  // NULL, not a delete: present-and-null is what tells the materialiser to
  // remove a pin that is already there. Deleting the key would mean "leave it
  // alone", which is the opposite of what the button says.
  assetPinRemove() {
    const answer = this.formData._asset_select
    if (!answer) return

    answer.floorplan_pin = null
    if (this.assetState) this.assetState.serverPin = null
    this.scheduleSave()
    this.renderCurrentStep()
  }

  // The JS half of InspectionValidator#validate_asset_selection, and
  // deliberately the same rules: required takes EITHER branch — a resolved
  // existing asset or a fresh-tag record — and a required step refuses an
  // untagged one, because Required IS the tag mandate (decision-41).
  validateAssetSelectStep() {
    if (this.assetSelectionSatisfied()) return true

    this.assetError(this.assetSelectionMessage())
    return false
  }

  assetSelectionMessage() {
    const phase = this.assetState?.phase
    if (phase === "foreign") return "Add the tag to your sites to continue — this project requires a tagged asset."
    if (phase === "fresh" || phase === "untagged") {
      if (!this.formData._asset_select?.product_type_id) return "Choose the product you are recording."
      return "Scan a tag — this project requires a tagged asset."
    }
    return "Scan or look up the tag to continue — this project requires a tagged asset."
  }

  renderSiteSelectStep() {
    // The wrapper this step last rendered is about to be thrown away —
    // destroy its Tom Select instances first, or their document-level
    // listeners outlive the DOM nodes they were bound to.
    this.destroySiteTomSelect()
    this.destroyHierarchyTomSelects()

    const container = this.fieldsContainerTarget
    container.innerHTML = ""

    const siteSelect = this.schemaValue.site_select
    const config = siteSelect.config || {}
    const sitesData = siteSelect.sites_data || []
    // Config booleans are cast server-side in resolved_site_select_config, so
    // these are real booleans rather than the "true"/"1" strings the old
    // add_location flag had to defend against.
    const allowCreation = config.allow_site_creation !== false
    const showBlock     = config.show_block !== false
    const showElevation = showBlock && config.show_elevation !== false
    // Location is deliberately not chained to the floor (decision-6): captured
    // with no floor answered, it attaches directly to the site.
    const showLocation  = config.show_location !== false
    const currentData = (typeof this.formData._site_select === "object" && this.formData._site_select !== null)
      ? this.formData._site_select : {}
    const isCreating = currentData.action === "new"
    const selectedSiteId = currentData.site_id || ""

    // The select itself stays empty — attachSiteSelectListeners feeds Tom
    // Select its options directly (name/address/uprn/client_uprn/postcode,
    // searchable), because those extra fields don't round-trip through plain
    // <option> tags (TASK-257, board 03). The "Create a new site…" row is
    // the combobox's own last option now, replacing the link this used to be.
    const dropdownHtml = `
      <div data-site-select-dropdown ${isCreating ? 'class="hidden"' : ''}>
        <label class="label">Site<span class="text-red-500 ml-1">*</span></label>
        <select class="w-full [&>*:first-child]:!cursor-pointer" style="visibility:hidden" autocomplete="off" data-site-select-input></select>
      </div>
    `

    const newSiteFormHtml = allowCreation ? `
      <div data-site-create-form ${isCreating ? "" : 'class="hidden"'}>
        <div class="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
          <p class="text-sm font-medium text-gray-700">New Site Details</p>
          <div>
            <label class="label">Site Name<span class="text-red-500 ml-1">*</span></label>
            <input type="text" class="form-control" placeholder="e.g. London Head Office"
                   value="${this.escapeHtml(currentData.site?.name || '')}"
                   data-site-new-field="name">
          </div>
          <div>
            <label class="label">Address Line 1</label>
            <input type="text" class="form-control" placeholder="e.g. 1 Example Street"
                   value="${this.escapeHtml(currentData.site?.address?.line_1 || '')}"
                   data-site-new-field="address.line_1">
          </div>
          <div>
            <label class="label">Address Line 2</label>
            <input type="text" class="form-control"
                   value="${this.escapeHtml(currentData.site?.address?.line_2 || '')}"
                   data-site-new-field="address.line_2">
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="label">Town / City</label>
              <input type="text" class="form-control" placeholder="e.g. Manchester"
                     value="${this.escapeHtml(currentData.site?.address?.town_or_city || '')}"
                     data-site-new-field="address.town_or_city">
            </div>
            <div>
              <label class="label">County</label>
              <input type="text" class="form-control"
                     value="${this.escapeHtml(currentData.site?.address?.county || '')}"
                     data-site-new-field="address.county">
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="label">Postcode<span class="text-red-500 ml-1">*</span></label>
              <input type="text" class="form-control" placeholder="e.g. M1 1AA"
                     value="${this.escapeHtml(currentData.site?.address?.postcode || '')}"
                     data-site-new-field="address.postcode">
            </div>
            <div>
              <label class="label">Country</label>
              <input type="text" class="form-control" placeholder="e.g. United Kingdom"
                     value="${this.escapeHtml(currentData.site?.address?.country || 'United Kingdom')}"
                     data-site-new-field="address.country">
            </div>
          </div>
        </div>
        <button type="button"
                class="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                data-site-cancel-create>
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Select an existing site instead
        </button>
      </div>
    ` : ""

    // Block gates floor (elevations.block_id is NOT NULL server-side), but
    // location stands alone since decision-6: answered with no floor, it is
    // saved directly against the site. A block left blank leaves the
    // inspection unassigned rather than inventing a "Default Block".
    //
    // The controls themselves are rendered by renderHierarchy(), because they
    // are re-rendered whenever the site or a parent level changes. The tree is
    // fetched from /sites/:id/hierarchy.json on selection and never preloaded
    // into the schema — see backlog/decisions/decision-1.
    const hierarchyHtml = (showBlock || showElevation || showLocation) ? `
      <div class="mt-4 pt-4 border-t border-gray-200">
        <div data-ss-hierarchy aria-live="polite"></div>
      </div>
    ` : ""

    // The customer reference ("Your Ref") is spine config, not a form field —
    // it lives on the first step next to the site so a required reference can
    // actually be entered (it used to be uncapturable, making required mode a
    // guaranteed validation dead end).
    const refCfg = this.schemaValue.header?.customer_reference || {}
    const yourRefHtml = refCfg.enabled ? `
      <div class="mb-4 pb-4 border-b border-gray-200">
        <label class="label">${this.escapeHtml(refCfg.label || "Your Ref")}${refCfg.required ? '<span class="text-red-500 ml-1">*</span>' : ""}</label>
        <input type="text" class="form-control" maxlength="100"
               value="${this.escapeHtml(this.customerReference || "")}"
               data-customer-reference-input>
      </div>
    ` : ""

    const wrapper = document.createElement("div")
    wrapper.className = "form-field"
    wrapper.innerHTML = `
      ${yourRefHtml}
      <div data-site-select-container>
        ${sitesData.length === 0 && !allowCreation ? `
          <p class="text-sm text-gray-500 italic">No sites available.</p>
        ` : `
          ${dropdownHtml}
          ${newSiteFormHtml}
        `}
        ${hierarchyHtml}
        <div class="mt-3 flex flex-wrap items-center gap-1.5" data-ss-path></div>
      </div>
    `
    container.appendChild(wrapper)

    const refInput = wrapper.querySelector("[data-customer-reference-input]")
    if (refInput) {
      refInput.addEventListener("input", () => {
        this.customerReference = refInput.value
        if (this.siteFieldErrors) delete this.siteFieldErrors.customer_reference
        refInput.classList.remove("error")
        const next = refInput.nextElementSibling
        if (next && next.dataset && "siteFieldError" in next.dataset) next.remove()
        this.scheduleSave()
      })
    }

    this.attachSiteSelectListeners(wrapper)
    this.renderHierarchy(wrapper)
    this.applySiteFieldErrors(wrapper)
    this.renderSitePath(wrapper)

    // A resumed draft already names a site, so fetch its tree on first paint
    // rather than waiting for a change event that will never come.
    if (selectedSiteId && !this.siteHierarchy) {
      this.loadHierarchy(selectedSiteId).then(() => this.renderHierarchy(wrapper))
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Site hierarchy: dependent Block → Floor → Location selectors
  //
  // The tree is fetched per selected site rather than preloaded. Inlining it
  // into the schema would be O(sites x blocks x elevations x locations) of
  // markup on a page that usually needs one site's worth.
  //
  // Each level is either a selection or a new entry, never both — that is the
  // submit contract the server enforces (backlog/decisions/decision-4), so the
  // UI has to make it impossible to supply both.
  // ─────────────────────────────────────────────────────────────────────────

  HIERARCHY_LEVELS = ["block", "elevation", "location"]

  siteSelectData() {
    const current = this.formData._site_select
    return (typeof current === "object" && current !== null) ? current : {}
  }

  // Which entry mode each level is in. Defaults to selecting, and falls back to
  // entering a new value when there is nothing at that level to select — a
  // dropdown with no options and no way past it is a dead end.
  hierarchyMode(level) {
    if (!this.hierarchyModes) this.hierarchyModes = {}
    if (this.hierarchyModes[level]) return this.hierarchyModes[level]

    const stored = this.siteSelectData()[level]
    if (stored && typeof stored === "object" && stored.id) return "select"
    if (stored) return "new"

    return this.hierarchyOptionsFor(level).length ? "select" : "new"
  }

  setHierarchyMode(level, mode) {
    if (!this.hierarchyModes) this.hierarchyModes = {}
    this.hierarchyModes[level] = mode
  }

  // The records selectable at a level, given what is selected above it.
  hierarchyOptionsFor(level) {
    const tree = this.siteHierarchy
    if (!tree) return []

    const blocks = tree.blocks || []
    if (level === "block") return blocks

    const block = blocks.find(b => b.id === this.selectedHierarchyId("block"))
    if (level === "elevation") return block ? (block.elevations || []) : []

    // Location (decision-6): a floor answered by id offers its own locations;
    // a floor answered by name offers none (the free-typed location nests
    // under the new floor at submit); no floor answered offers the site-level
    // list from the hierarchy payload's root.
    if (!this.hierarchyLevelAnswered("elevation")) return tree.locations || []

    const elevation = block ? (block.elevations || []).find(e => e.id === this.selectedHierarchyId("elevation")) : null
    return elevation ? (elevation.locations || []) : []
  }

  selectedHierarchyId(level) {
    const stored = this.siteSelectData()[level]
    return (stored && typeof stored === "object") ? (stored.id || "") : ""
  }

  storedNewValue(level) {
    const stored = this.siteSelectData()[level]
    if (!stored) return ""
    if (typeof stored === "string") return stored
    return stored.level || stored.name || ""
  }

  async loadHierarchy(siteId) {
    if (!siteId) {
      this.siteHierarchy = null
      return
    }

    this.hierarchyLoading = true
    this.hierarchyError = null

    try {
      const response = await fetch(`/sites/${siteId}/hierarchy.json`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.siteHierarchy = await response.json()
    } catch (error) {
      // A failed fetch must not block the submission. Every level falls back to
      // free text, which is what the whole flow was before this change.
      this.siteHierarchy = null
      this.hierarchyError = "Could not load this site's blocks and floors. You can still type them in."
    } finally {
      this.hierarchyLoading = false
    }
  }

  renderHierarchy(wrapper) {
    const host = wrapper.querySelector("[data-ss-hierarchy]")
    if (!host) return

    const siteSelect = this.schemaValue.site_select
    const config = siteSelect.config || {}
    const showBlock = config.show_block !== false
    const showElevation = showBlock && config.show_elevation !== false
    const showLocation = config.show_location !== false

    if (this.hierarchyLoading) {
      host.innerHTML = `<p class="text-sm text-gray-500">Loading this site's blocks and floors…</p>`
      return
    }

    const notice = this.hierarchyError
      ? `<p class="text-sm text-amber-700 mb-3">${this.escapeHtml(this.hierarchyError)}</p>`
      : ""

    // Any Tom Select instance the previous paint of this host created must
    // go before the markup it is wrapping does, or its document-level
    // listeners (click-outside, keyboard nav) outlive the dropdown they
    // belong to.
    this.destroyHierarchyTomSelects()

    host.innerHTML = notice +
      (showBlock ? this.hierarchyLevelHtml("block", "Block", config.require_block === true) : "") +
      (showElevation ? this.hierarchyLevelHtml("elevation", "Floor", config.require_elevation !== false) : "") +
      (showLocation ? this.hierarchyLevelHtml("location", "Location", config.require_location !== false) : "")

    this.attachHierarchyListeners(wrapper, host)
    // Every rebuild of this host destroys any error decorations it carried —
    // including the async rebuild after the tree fetch resolves, which runs
    // long after applySiteFieldErrors. Hierarchy errors are therefore applied
    // here, on every rebuild, not in the one-shot step render.
    this.applyHierarchyFieldErrors(host)
    // The plain-words recap depends on whichever levels this rebuild just
    // answered or cleared, so it refreshes alongside the host on every call —
    // the one place that needs to remember this (TASK-257, board 03).
    this.renderSitePath(wrapper)
  }

  // Display labels for the hierarchy typeaheads — shared between the markup
  // builder and the Tom Select instances attachHierarchyListeners wires up.
  HIERARCHY_LABELS = { block: "Block", elevation: "Floor", location: "Location" }

  hierarchyLevelHtml(level, label, required) {
    const options = this.hierarchyOptionsFor(level)
    const mode = this.hierarchyMode(level)
    const requiredMark = required
      ? `<span class="text-red-500 ml-1">*</span>`
      : `<span class="text-xs text-gray-400 font-normal">(optional)</span>`

    // A level whose parent has not been chosen yet has nothing to offer.
    const parentChosen = this.hierarchyParentChosen(level)
    if (!parentChosen) {
      return `
        <div class="mb-3" data-ss-level="${level}">
          <label class="label">${label} ${requiredMark}</label>
          <p class="text-sm text-gray-500 italic">${this.escapeHtml(this.hierarchyParentPrompt(level))}</p>
        </div>`
    }

    const emptyNote = options.length === 0
      ? `<p class="text-sm text-gray-500 italic mb-2">No ${label.toLowerCase()}s recorded for this site yet.</p>`
      : ""

    // The select stays empty here — attachHierarchyListeners (via
    // instantiateHierarchyTomSelect) is where Tom Select gets its options,
    // and where "Add new <level>…" becomes the combobox's own last option,
    // replacing the separate link this used to be (TASK-257, board 03).
    const selectHtml = options.length === 0 ? emptyNote : `
      <div data-ss-select-wrap ${mode === "new" ? 'class="hidden"' : ""}>
        <select class="w-full [&>*:first-child]:!cursor-pointer" style="visibility:hidden" autocomplete="off" data-ss-select="${level}"></select>
      </div>`

    const newInput = level === "elevation"
      ? `<input type="number" step="1" class="form-control min-h-[44px]" data-ss-new="${level}"
                placeholder="Floor number (-5 to 50)" value="${this.escapeHtml(String(this.storedNewValue(level)))}">
         <p class="text-xs text-gray-500 mt-1">Whole numbers only, -5 to 50</p>`
      : `<input type="text" class="form-control min-h-[44px]" data-ss-new="${level}"
                placeholder="e.g. ${level === "block" ? "Block A" : "Reception"}"
                value="${this.escapeHtml(String(this.storedNewValue(level)))}">`

    const backToSelect = options.length === 0 ? "" : `
      <button type="button" class="mt-2 min-h-[44px] text-sm text-gray-500 flex items-center gap-1"
              data-ss-pick-existing="${level}">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Choose an existing ${label.toLowerCase()} instead
      </button>`

    return `
      <div class="mb-3" data-ss-level="${level}">
        <label class="label">${label} ${requiredMark}</label>
        ${selectHtml}
        <div data-ss-new-wrap ${mode === "new" || options.length === 0 ? "" : 'class="hidden"'}>
          ${newInput}
          ${backToSelect}
        </div>
        ${level === "location" ? this.siteLevelLocationNote() : ""}
        ${level === "location" ? this.locationHelpText(options) : ""}
      </div>`
  }

  // Where the location will be filed must never be a surprise: with the floor
  // offered but unanswered, say so. Silent when the floor is hidden (flat
  // capture has nothing to explain) and when a floor is answered (it nests).
  siteLevelLocationNote() {
    const config = this.schemaValue.site_select?.config || {}
    const showElevation = config.show_block !== false && config.show_elevation !== false
    if (!showElevation || this.hierarchyLevelAnswered("elevation")) return ""

    return `
      <p class="mt-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700"
         data-ss-site-level-note>
        No floor chosen — this location will be saved against the site.
      </p>`
  }

  // A long location list is where the typeahead earns its keep — past 10
  // options, say so and name what's being searched (TASK-257, board 03).
  locationHelpText(options) {
    if (options.length <= 10) return ""

    const floorAnswered = this.hierarchyLevelAnswered("elevation")
    const subject = floorAnswered ? (this.hierarchyLevelLabel("elevation") || "this floor") : "the site"

    return `
      <p class="mt-1 text-xs text-gray-500" data-ss-location-help>
        Type to search ${this.escapeHtml(subject)}'s ${options.length} locations, or add a new one from the list.
      </p>`
  }

  // THE SITE STEP NO LONGER PINS (TASK-177.3, decision-41). Capture's pin
  // moved onto the ASSET and lives on the QTag step, which is where the thing
  // being pinned is chosen; this step's old "Set position on floor plan"
  // button pinned the Inspection and is gone.
  //
  // Nothing about the OTHER pin paths changed, and none of them is on its way
  // out (decision-44): `_site_select.floorplan_pin` is a permanent channel that
  // materialises onto the Inspection (decision-34) — the device's Where step
  // writes it, and it is the only pin a submission with no asset can carry —
  // and the submission-detail and site-detail pinning flows remain as
  // correction tools. What this step dropped is the WEB's duplicate entry point
  // into that channel, not the channel.

  // A floor cannot be chosen before a block — elevations.block_id is NOT NULL,
  // there is no orphan to hang it on. Location is reachable without a floor
  // (decision-6), except while a required floor is still unanswered: the floor
  // must come first anyway, and gating keeps the flow honest.
  hierarchyParentChosen(level) {
    if (level === "block") return true
    if (level === "elevation") return this.hierarchyLevelAnswered("block")

    if (this.hierarchyLevelAnswered("elevation")) return true
    const config = this.schemaValue.site_select?.config || {}
    const showElevation = config.show_block !== false && config.show_elevation !== false
    return !(showElevation && config.require_elevation !== false)
  }

  hierarchyLevelAnswered(level) {
    const stored = this.siteSelectData()[level]
    if (!stored) return false
    if (typeof stored === "string") return stored.trim() !== ""
    return Boolean(stored.id || stored.name || stored.level)
  }

  hierarchyParentPrompt(level) {
    return level === "elevation" ? "Choose or add a block first." : "Choose or add a floor first."
  }

  // Writes the tagged value the server expects: exactly one of id or the
  // level's new-value key, never both.
  setHierarchyValue(level, { id, value }) {
    const data = { ...this.siteSelectData() }

    if (id) {
      data[level] = { id: id }
    } else if (value !== undefined && String(value).trim() !== "") {
      data[level] = level === "elevation" ? { level: String(value).trim() } : { name: String(value).trim() }
    } else {
      delete data[level]
    }

    // An answered level has resolved its own validation error; keep stale
    // decorations from re-appearing on the next hierarchy rebuild.
    if (data[level] && this.siteFieldErrors) delete this.siteFieldErrors[level]

    // Everything below a changed level is stale — a floor belongs to the block
    // that was just replaced, so keeping it would submit a mismatched pair the
    // server rejects.
    const below = this.HIERARCHY_LEVELS.slice(this.HIERARCHY_LEVELS.indexOf(level) + 1)
    below.forEach(deeper => {
      delete data[deeper]
      if (this.hierarchyModes) delete this.hierarchyModes[deeper]
    })

    this.formData._site_select = data
    this.scheduleSave()
  }

  // Tom Select instances this host currently owns, keyed by level — torn
  // down in destroyHierarchyTomSelects() before every rebuild of the host
  // (renderHierarchy) so a stale instance never outlives its <select>.
  destroyHierarchyTomSelects() {
    if (this.hierarchyTomSelects) {
      Object.values(this.hierarchyTomSelects).forEach(ts => ts.destroy())
    }
    this.hierarchyTomSelects = {}
  }

  // Block/Floor/Location become the same kind of combobox the site select
  // is (TASK-257, board 03): searchable by name (Floor also by its level),
  // with "Add new <level>…" as the list's own last option. Selecting that
  // row switches the level into "new" mode instead of adding a real option —
  // setHierarchyValue/renderHierarchy already know how to draw that mode.
  instantiateHierarchyTomSelect(wrapper, level, selectEl) {
    const label = this.HIERARCHY_LABELS[level] || level
    const options = this.hierarchyOptionsFor(level).map(option => ({
      id: option.id,
      name: option.name,
      level: option.level,
      levelSearch: level === "elevation" ? `level ${option.level}` : undefined
    }))
    const selectedId = this.selectedHierarchyId(level)
    const optionLabel = (data) => level === "elevation" ? `${data.name} (level ${data.level})` : data.name

    const ts = new TomSelect(selectEl, {
      options,
      items: selectedId ? [ selectedId ] : [],
      valueField: "id",
      labelField: "name",
      searchField: level === "elevation" ? [ "name", "levelSearch" ] : [ "name" ],
      maxOptions: null,
      plugins: [ "clear_button" ],
      placeholder: `Search or add a ${label.toLowerCase()}…`,
      create: (input) => {
        const text = input.trim()
        return text ? { id: `__new_${level}__${text}`, name: text, _create: true } : false
      },
      render: {
        option: (data, escape) => data._create
          ? `<div class="create">Add <strong>${escape(data.name)}</strong>&hellip;</div>`
          : `<div class="option"><span>${escape(optionLabel(data))}</span></div>`,
        item: (data, escape) => `<div>${escape(data._create ? data.name : optionLabel(data))}</div>`,
        option_create: (data, escape) =>
          `<div class="create">Add new ${escape(label.toLowerCase())} &ldquo;<strong>${escape(data.input)}</strong>&rdquo;&hellip;</div>`
      },
      onChange: (value) => {
        if (!value) {
          this.setHierarchyValue(level, {})
          this.renderHierarchy(wrapper)
          return
        }

        const data = ts.options[value]
        if (data && data._create) {
          // A pseudo-option, not a real record — drop it and switch the
          // level into its existing "new" mode instead of adding it to the
          // list (TASK-257, board 03).
          ts.clear(true)
          ts.removeOption(value)
          this.setHierarchyMode(level, "new")
          this.setHierarchyValue(level, { value: data.name })
          this.renderHierarchy(wrapper)
          return
        }

        this.setHierarchyValue(level, { id: value })
        this.renderHierarchy(wrapper)
      }
    })
    this.createOptionLast(ts)

    selectEl.style.visibility = "visible"
    this.hierarchyTomSelects[level] = ts
  }

  attachHierarchyListeners(wrapper, host) {
    this.HIERARCHY_LEVELS.forEach(level => {
      const selectEl = host.querySelector(`[data-ss-select="${level}"]`)
      if (selectEl) this.instantiateHierarchyTomSelect(wrapper, level, selectEl)
    })

    host.querySelectorAll("[data-ss-new]").forEach(input => {
      input.addEventListener("input", () => {
        this.setHierarchyValue(input.dataset.ssNew, { value: input.value })
        this.renderSitePath(wrapper)
      })
      // Re-render on blur rather than on every keystroke: the levels below
      // depend on this one, and rebuilding them mid-word would steal focus.
      input.addEventListener("blur", () => this.renderHierarchy(wrapper))
    })

    host.querySelectorAll("[data-ss-pick-existing]").forEach(button => {
      button.addEventListener("click", () => {
        const level = button.dataset.ssPickExisting
        this.setHierarchyMode(level, "select")
        this.setHierarchyValue(level, {})
        this.renderHierarchy(wrapper)
      })
    })
  }

  decorateSiteFieldError(input, message) {
    if (!input || !message) return
    input.classList.add('error')
    const errEl = document.createElement('p')
    errEl.className = 'text-red-600 text-sm mt-1'
    errEl.dataset.siteFieldError = ''
    errEl.textContent = message
    input.insertAdjacentElement('afterend', errEl)
  }

  // The non-hierarchy decorations only — the hierarchy host redraws itself
  // (including asynchronously after the tree fetch), so its levels are
  // decorated by renderHierarchy via applyHierarchyFieldErrors instead.
  applySiteFieldErrors(wrapper) {
    const errs = this.siteFieldErrors || {}
    if (errs.customer_reference) this.decorateSiteFieldError(wrapper.querySelector('[data-customer-reference-input]'), errs.customer_reference)
    if (errs.site) this.decorateSiteFieldError(wrapper.querySelector('[data-site-select-input]'), errs.site)
    if (errs.site_name) this.decorateSiteFieldError(wrapper.querySelector('[data-site-new-field="name"]'), errs.site_name)
  }

  applyHierarchyFieldErrors(host) {
    const errs = this.siteFieldErrors || {}
    this.HIERARCHY_LEVELS.forEach(level => {
      if (!errs[level]) return

      this.decorateSiteFieldError(
        host.querySelector(`[data-ss-new="${level}"], [data-ss-select="${level}"]`), errs[level]
      )
    })
  }

  // The site Tom Select instance this step currently owns — destroyed in
  // destroySiteTomSelect() before every full re-render (renderSiteSelectStep)
  // so a stale instance never outlives its <select>.
  // Board 03 draws "Create a new site …" / "Add new …" as the LAST row of each
  // list; Tom Select always prepends its create row. Move it after every
  // refresh. The first real match stays the active (Enter) choice either way.
  createOptionLast(ts) {
    ts.hook("after", "refreshOptions", () => {
      const create = ts.dropdown_content.querySelector(".create")
      if (create) ts.dropdown_content.appendChild(create)
    })
  }

  destroySiteTomSelect() {
    if (this.siteTomSelect) {
      this.siteTomSelect.destroy()
      this.siteTomSelect = null
    }
  }

  attachSiteSelectListeners(wrapper) {
    const siteContainer = wrapper.querySelector('[data-site-select-container]')
    if (!siteContainer) return

    const dropdown = siteContainer.querySelector('[data-site-select-dropdown]')
    const createForm = siteContainer.querySelector('[data-site-create-form]')
    const siteSelectInput = siteContainer.querySelector('[data-site-select-input]')
    const cancelCreate = siteContainer.querySelector('[data-site-cancel-create]')

    const clearVisualError = (input) => {
      if (!input) return
      input.classList.remove('error')
      const next = input.nextElementSibling
      if (next && next.dataset && 'siteFieldError' in next.dataset) next.remove()
    }

    const clearKey = (key) => {
      if (this.siteFieldErrors) delete this.siteFieldErrors[key]
    }

    // Switches the step into "new site" mode with the typed (or carried-
    // forward) name pre-filled — what used to be the "Create new site
    // instead" link's job, now reached from the combobox's own create row
    // (TASK-257, board 03).
    const switchToCreateMode = (name) => {
      if (dropdown) dropdown.classList.add('hidden')
      if (createForm) createForm.classList.remove('hidden')
      const nameInput = createForm?.querySelector('[data-site-new-field="name"]')
      if (nameInput) nameInput.value = name || ''
      this.formData._site_select = { action: 'new', site: { name: name || '', address: {} } }
      this.scheduleSave()
      this.renderSitePath(wrapper)
    }

    const selectExistingSite = async (siteId, siteName) => {
      // Answers below belong to the site being replaced.
      this.HIERARCHY_LEVELS.forEach(level => { delete this.formData._site_select?.[level] })
      this.hierarchyModes = {}

      this.formData._site_select = { action: 'existing', site_id: siteId, site_name: siteName }
      this.scheduleSave()
      clearKey('site')
      clearVisualError(siteSelectInput)

      this.renderHierarchy(wrapper)
      await this.loadHierarchy(siteId)
      this.renderHierarchy(wrapper)
    }

    if (siteSelectInput) {
      this.destroySiteTomSelect()

      const siteOptions = (this.schemaValue.site_select?.sites_data || []).map(site => ({
        id: site.id,
        name: site.name,
        address: site.address || "",
        uprn: site.uprn || "",
        client_uprn: site.client_uprn || "",
        postcode: site.postcode || "",
        group: site.in_project ? "in_project" : "other"
      }))
      const hasInProjectGroup = siteOptions.some(option => option.group === "in_project")
      const hasOtherGroup = siteOptions.some(option => option.group === "other")

      const sd = this.siteSelectData()
      const isNew = sd.action === 'new'
      const selectedSiteId = !isNew ? (sd.site_id || '') : ''

      const ts = new TomSelect(siteSelectInput, {
        options: siteOptions,
        items: selectedSiteId ? [ selectedSiteId ] : [],
        valueField: "id",
        labelField: "name",
        // Searchable by name, UPRN, client UPRN, address or postcode
        // (board 03) — postcode is its own field even though the address
        // summary usually already carries it, so "N1 1AA" still matches a
        // site whose primary address has no line_1 to build a summary from.
        searchField: [ "name", "address", "uprn", "client_uprn", "postcode" ],
        maxOptions: null,
        plugins: [ "clear_button" ],
        placeholder: "Search sites by name, UPRN or address…",
        optgroupField: "group",
        optgroups: [
          ...(hasInProjectGroup ? [ { value: "in_project", label: "In this project" } ] : []),
          ...(hasOtherGroup ? [ { value: "other", label: "Other team sites" } ] : [])
        ],
        create: (input) => {
          const text = input.trim()
          return text ? { id: `__new_site__${text}`, name: text, _create: true } : false
        },
        render: {
          option: (data, escape) => {
            if (data._create) {
              return `<div class="create">Create a new site &ldquo;<strong>${escape(data.name)}</strong>&rdquo;&hellip;</div>`
            }
            const bits = [ data.address, data.uprn ? `<span class="font-mono">${escape(data.uprn)}</span>` : "", data.client_uprn ? `Client ${escape(data.client_uprn)}` : "" ].filter(Boolean)
            return `
              <div class="option">
                <div class="font-medium text-gray-900">${escape(data.name)}</div>
                ${bits.length ? `<div class="text-xs text-gray-500">${bits.join(" &middot; ")}</div>` : ""}
              </div>`
          },
          item: (data, escape) => `<div>${escape(data._create ? data.name : data.name)}</div>`,
          option_create: (data, escape) =>
            `<div class="create">Create a new site &ldquo;<strong>${escape(data.input)}</strong>&rdquo;&hellip;</div>`
        },
        onChange: (value) => {
          if (!value) {
            this.formData._site_select = {}
            this.scheduleSave()
            this.renderHierarchy(wrapper)
            return
          }

          const data = ts.options[value]
          if (data && data._create) {
            ts.clear(true)
            ts.removeOption(value)
            switchToCreateMode(data.name)
            return
          }

          selectExistingSite(value, data?.name || '')
        }
      })

      siteSelectInput.style.visibility = "visible"
      this.siteTomSelect = ts
      this.createOptionLast(ts)

      // "If the list has exactly one site and nothing is chosen yet, pre-
      // select it" (board 03) — fires the same onChange path a real pick
      // would, so hierarchy loading and persistence both run.
      if (siteOptions.length === 1 && !selectedSiteId && !isNew) {
        ts.setValue(siteOptions[0].id)
      }
    }

    if (cancelCreate && dropdown && createForm) {
      cancelCreate.addEventListener('click', () => {
        createForm.classList.add('hidden')
        dropdown.classList.remove('hidden')

        const currentValue = this.siteTomSelect?.getValue()
        if (currentValue) {
          const data = this.siteTomSelect.options[currentValue]
          selectExistingSite(currentValue, data?.name || '')
        } else {
          this.formData._site_select = { action: 'existing', site_id: '', site_name: '' }
          this.scheduleSave()
          this.renderSitePath(wrapper)
        }
      })
    }

    if (createForm) {
      createForm.querySelectorAll('input').forEach(input => {
        input.addEventListener('input', () => {
          const siteData = { name: '', address: {} }
          createForm.querySelectorAll('[data-site-new-field]').forEach(field => {
            const path = field.dataset.siteNewField
            if (path.startsWith('address.')) {
              siteData.address[path.replace('address.', '')] = field.value
            } else {
              siteData[path] = field.value
            }
          })
          const base = { action: 'new', site: siteData }
          const existing = this.siteSelectData()
          this.HIERARCHY_LEVELS.forEach(level => {
            if (existing[level] !== undefined) base[level] = existing[level]
          })
          this.formData._site_select = base
          this.scheduleSave()
          this.renderSitePath(wrapper)

          if (input.dataset.siteNewField === 'name') {
            clearKey('site_name')
            clearVisualError(input)
          }
        })
      })
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // The plain-words recap: "Recording at Site › Block › Floor › Location"
  // (TASK-257, board 03). siteSelectionPathText() is the bare string, read
  // by builder A's Review rows too — one source so the step's own recap and
  // Review can never drift apart; siteSelectionPathHtml()/renderSitePath()
  // are this step's own chip rendering of the same levels.
  // ─────────────────────────────────────────────────────────────────────────

  siteSelectionPathText() {
    const parts = [ this.siteSelectionSiteLabel() ]
      .concat(this.HIERARCHY_LEVELS.map(level => this.hierarchyLevelLabel(level)))
      .filter(Boolean)

    return parts.join(" › ")
  }

  // The chosen site's display name — existing (from sites_data) or newly
  // typed. "" when nothing is answered yet.
  siteSelectionSiteLabel(sd = this.siteSelectData()) {
    if (sd.action === "new") return (sd.site?.name || "").trim()
    if (!sd.site_id) return ""

    const site = (this.schemaValue.site_select?.sites_data || []).find(s => s.id === sd.site_id)
    return site?.name || sd.site_name || ""
  }

  // A chosen hierarchy level's display text — existing (looked up in the
  // loaded tree) or newly typed. "" when the level has nothing answered.
  hierarchyLevelLabel(level) {
    const stored = this.siteSelectData()[level]
    if (!stored) return ""
    if (typeof stored === "string") return stored.trim()

    if (stored.id) {
      const option = this.hierarchyOptionsFor(level).find(o => o.id === stored.id)
      // The tree may not be loaded yet (e.g. this render runs before
      // loadHierarchy resolves) — nothing sensible to show until it is.
      if (!option) return ""
      return level === "elevation" ? `Floor ${option.level}` : option.name
    }

    if (level === "elevation" && stored.level !== undefined) return `Floor ${stored.level}`
    return stored.name || ""
  }

  // The first unanswered REQUIRED level, Site → Block → Floor → Location,
  // for the "<level> to choose" prompt — reads the same problem list the
  // rail badge and Next both read (siteSelectionProblems), so the three
  // surfaces can never disagree about what's still missing.
  nextUnansweredRequiredLevelLabel() {
    const labels = { site: "Site", site_name: "Site", block: "Block", elevation: "Floor", location: "Location" }
    const blank = this.siteSelectionProblems().find(problem => problem.blank)
    return blank ? (labels[blank.key] || null) : null
  }

  siteSelectionPathHtml() {
    const chipClass = "bg-blue-50 border border-blue-200 text-blue-800 rounded px-2 py-0.5 text-sm"
    const chip = (text) => `<span class="${chipClass}">${this.escapeHtml(text)}</span>`

    const chosen = [ this.siteSelectionSiteLabel() ]
      .concat(this.HIERARCHY_LEVELS.map(level => this.hierarchyLevelLabel(level)))
      .filter(Boolean)

    const fragments = chosen.map(chip)
    const next = this.nextUnansweredRequiredLevelLabel()
    if (next) fragments.push(`<span class="text-sm text-gray-400">${this.escapeHtml(next)} to choose</span>`)

    if (fragments.length === 0) return ""

    const sep = `<span class="text-gray-400 text-sm mx-0.5">&rsaquo;</span>`
    return `<span class="text-sm text-gray-500 mr-1">Recording at</span>${fragments.join(sep)}`
  }

  renderSitePath(wrapper) {
    const host = wrapper.querySelector("[data-ss-path]")
    if (host) host.innerHTML = this.siteSelectionPathHtml()
  }

  renderRepeatableStack(section) {
    const sectionKey = section.section_key
    const instanceCount = this.instanceCounts[sectionKey] || 1
    const minReps = section.min_repetitions || 1
    const maxReps = section.max_repetitions
    const container = this.fieldsContainerTarget
    container.innerHTML = ""

    for (let i = 0; i < instanceCount; i++) {
      const card = document.createElement("div")
      card.className = "border border-gray-200 rounded-lg p-4 mb-4"
      card.dataset.instanceIndex = i

      const header = document.createElement("div")
      header.className = "flex items-center justify-between mb-4 pb-3 border-b border-gray-100"

      const headerLabel = document.createElement("h4")
      headerLabel.className = "text-sm font-semibold text-gray-700"
      headerLabel.textContent = `${section.title} ${i + 1}`
      header.appendChild(headerLabel)

      if (instanceCount > minReps) {
        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.className = "text-sm text-red-600 hover:text-red-800"
        removeBtn.textContent = "Remove"
        removeBtn.dataset.action = "click->submission-wizard#removeInstance"
        removeBtn.dataset.instanceIndex = i
        header.appendChild(removeBtn)
      }

      card.appendChild(header)

      const fieldsWrapper = document.createElement("div")
      fieldsWrapper.className = "space-y-6"
      card.appendChild(fieldsWrapper)

      this.renderFieldsInto(section.fields, sectionKey, i, fieldsWrapper)

      container.appendChild(card)
    }

    if (!maxReps || instanceCount < maxReps) {
      const addBtn = document.createElement("button")
      addBtn.type = "button"
      addBtn.className = "btn-outline whitespace-nowrap"
      addBtn.textContent = `Add Another ${section.title}`
      addBtn.dataset.action = "click->submission-wizard#addInstance"
      container.appendChild(addBtn)
    }
  }

  renderFields(fields, sectionKey, instanceIndex = null) {
    const container = this.fieldsContainerTarget
    container.innerHTML = ""
    this.renderFieldsInto(fields, sectionKey, instanceIndex, container)
  }

  renderFieldsInto(fields, sectionKey, instanceIndex, container) {
    fields.forEach(field => {
      if (!this.isFieldVisible(field, sectionKey, instanceIndex)) return

      const fieldElement = this.createFieldElement(field, sectionKey, instanceIndex)
      container.appendChild(fieldElement)
    })
  }

  // =========================================================================
  // Conditional logic
  // =========================================================================

  isFieldVisible(field, sectionKey, instanceIndex) {
    if (!field.logic || !field.logic.visible_if) return true

    const condition = field.logic.visible_if
    if (!condition.field_key || !condition.operator) return true

    let targetValue
    if (instanceIndex !== null) {
      const instanceData = this.formData[sectionKey]?.[instanceIndex] || {}
      targetValue = instanceData[condition.field_key]
    } else {
      targetValue = this.formData[condition.field_key]
    }

    return this.evaluateCondition(condition.operator, targetValue, condition.value)
  }

  evaluateCondition(operator, actual, expected) {
    switch (operator) {
      case "equals": return String(actual) === String(expected)
      case "not_equals": return String(actual) !== String(expected)
      case "contains": return String(actual).includes(String(expected))
      case "greater_than": return parseFloat(actual) > parseFloat(expected)
      case "less_than": return parseFloat(actual) < parseFloat(expected)
      case "is_empty": return !actual || actual === ""
      case "is_not_empty": return actual && actual !== ""
      default: return true
    }
  }

  // =========================================================================
  // Field value management
  // =========================================================================

  getFieldValue(fieldKey, sectionKey, instanceIndex) {
    if (instanceIndex !== null) {
      return this.formData[sectionKey]?.[instanceIndex]?.[fieldKey] || ""
    }
    return this.formData[fieldKey] || ""
  }

  setFieldValue(fieldKey, value, sectionKey, instanceIndex) {
    if (instanceIndex !== null) {
      if (!this.formData[sectionKey]) this.formData[sectionKey] = []
      if (!this.formData[sectionKey][instanceIndex]) {
        this.formData[sectionKey][instanceIndex] = { _index: instanceIndex + 1, _label: "" }
      }
      this.formData[sectionKey][instanceIndex][fieldKey] = value
    } else {
      this.formData[fieldKey] = value
    }
    this.clearFieldError(sectionKey, instanceIndex, fieldKey)
    this.clearFieldVisualError(fieldKey, instanceIndex)
    this.scheduleSave()
  }

  clearFieldVisualError(fieldKey, instanceIndex) {
    const idxAttr = instanceIndex === null || instanceIndex === undefined ? "_" : String(instanceIndex)
    const wrapper = this.fieldsContainerTarget?.querySelector(`[data-field-key="${CSS.escape(fieldKey)}"][data-instance-index="${idxAttr}"]`)
    if (!wrapper) return
    wrapper.querySelectorAll('.form-control.error').forEach(el => el.classList.remove('error'))
    wrapper.querySelectorAll('[data-widget-error-outline]').forEach(el => {
      el.classList.remove('border', 'border-red-400', 'rounded-md', 'p-3')
      delete el.dataset.widgetErrorOutline
    })
    const errEl = wrapper.querySelector('[data-field-error]')
    if (errEl) errEl.remove()
  }

  // =========================================================================
  // Field creation
  // =========================================================================

  createFieldElement(field, sectionKey, instanceIndex) {
    const wrapper = document.createElement("div")
    wrapper.className = "form-field"
    wrapper.dataset.fieldKey = field.field_key
    wrapper.dataset.instanceIndex = instanceIndex === null || instanceIndex === undefined ? "_" : String(instanceIndex)

    const currentValue = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
    let html = ""

    switch (field.field_type) {
      case "text": html = this.renderTextField(field, currentValue); break
      case "textarea": html = this.renderTextareaField(field, currentValue); break
      case "number": html = this.renderNumberField(field, currentValue); break
      case "select": html = this.renderSelectField(field, currentValue); break
      case "radio": html = this.renderRadioField(field, currentValue, instanceIndex); break
      case "checkbox": html = this.renderCheckboxField(field, currentValue); break
      case "toggle": html = this.renderToggleField(field, currentValue); break
      case "date": html = this.renderDateField(field, currentValue); break
      case "time": html = this.renderTimeField(field, currentValue); break
      case "datetime": html = this.renderDateTimeField(field, currentValue); break
      case "rating": html = this.renderRatingField(field, currentValue); break
      case "photo": html = this.renderPhotoField(field, currentValue); break
      case "signature": html = this.renderSignatureField(field, currentValue); break
      case "product_select": html = this.renderProductSelectField(field, currentValue, instanceIndex); break
      case "product_installer": html = this.renderProductInstallerField(field, currentValue, sectionKey, instanceIndex); break
      case "qtag_scan": html = this.renderQtagScanField(field, currentValue); break
      case "section_break": html = this.renderSectionBreak(field); break
      default: html = this.renderTextField(field, currentValue)
    }

    wrapper.innerHTML = html

    wrapper.querySelectorAll('[required]').forEach(el => el.removeAttribute('required'))
    wrapper.querySelectorAll('[pattern]').forEach(el => el.removeAttribute('pattern'))
    wrapper.querySelectorAll('input[type="number"][min], input[type="number"][max]').forEach(el => {
      el.removeAttribute('min')
      el.removeAttribute('max')
    })
    wrapper.querySelectorAll('[minlength], [maxlength]').forEach(el => {
      el.removeAttribute('minlength')
      el.removeAttribute('maxlength')
    })

    this.attachFieldListeners(wrapper, field, sectionKey, instanceIndex)

    const errorKey = this.fieldErrorKey(sectionKey, instanceIndex, field.field_key)
    const errorMsg = this.fieldErrors[errorKey]
    if (errorMsg) {
      const formControls = wrapper.querySelectorAll('.form-control')
      if (formControls.length > 0) {
        formControls.forEach(el => el.classList.add('error'))
      } else {
        const widget = wrapper.querySelector('label + div') || wrapper.querySelector('div')
        if (widget) {
          widget.classList.add('border', 'border-red-400', 'rounded-md', 'p-3')
          widget.dataset.widgetErrorOutline = ''
        }
      }
      const errorEl = document.createElement('p')
      errorEl.className = 'text-red-600 text-sm mt-1'
      errorEl.dataset.fieldError = ''
      errorEl.textContent = errorMsg
      wrapper.appendChild(errorEl)
    }

    return wrapper
  }

  // =========================================================================
  // Field renderers
  // =========================================================================

  renderLabel(field) {
    return `<label class="label">${field.label}${field.required ? '<span class="text-red-500 ml-1">*</span>' : ''}</label>`
  }

  renderTextField(field, value) {
    return `
      ${this.renderLabel(field)}
      <input type="text" class="form-control" value="${this.escapeHtml(value)}"
             placeholder="${field.label}" ${field.required ? 'required' : ''} data-field-input>
    `
  }

  renderTextareaField(field, value) {
    return `
      ${this.renderLabel(field)}
      <textarea class="form-control" rows="4" placeholder="${field.label}"
                ${field.required ? 'required' : ''} data-field-input>${this.escapeHtml(value)}</textarea>
    `
  }

  renderNumberField(field, value) {
    const v = field.validations || {}
    return `
      ${this.renderLabel(field)}
      <input type="number" class="form-control" value="${value}"
             placeholder="${field.label}"
             ${v.min !== undefined ? `min="${v.min}"` : ''}
             ${v.max !== undefined ? `max="${v.max}"` : ''}
             ${field.required ? 'required' : ''} data-field-input>
    `
  }

  renderSelectField(field, value) {
    const options = field.options?.options || []
    return `
      ${this.renderLabel(field)}
      <select class="form-control" ${field.required ? 'required' : ''} data-field-input>
        <option value="">Select...</option>
        ${options.map(opt => `
          <option value="${this.escapeHtml(opt.value)}" ${value === opt.value ? 'selected' : ''}>
            ${this.escapeHtml(opt.label)}
          </option>
        `).join('')}
      </select>
    `
  }

  renderRadioField(field, value, instanceIndex) {
    const options = field.options?.options || []
    const name = `${field.field_key}_${instanceIndex !== null ? instanceIndex : 'single'}`
    return `
      ${this.renderLabel(field)}
      <div class="space-y-2 mt-2">
        ${options.map(opt => `
          <div class="flex items-center">
            <input type="radio" name="${name}" value="${this.escapeHtml(opt.value)}"
                   ${value === opt.value ? 'checked' : ''}
                   class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500" data-field-input>
            <label class="ml-3 text-sm text-gray-700">${this.escapeHtml(opt.label)}</label>
          </div>
        `).join('')}
      </div>
    `
  }

  renderCheckboxField(field, value) {
    const options = field.options?.options || []
    const selectedValues = Array.isArray(value) ? value : []
    return `
      ${this.renderLabel(field)}
      <div class="space-y-2 mt-2">
        ${options.map(opt => `
          <div class="flex items-center">
            <input type="checkbox" value="${this.escapeHtml(opt.value)}"
                   ${selectedValues.includes(opt.value) ? 'checked' : ''}
                   class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                   data-field-input data-checkbox-group>
            <label class="ml-3 text-sm text-gray-700">${this.escapeHtml(opt.label)}</label>
          </div>
        `).join('')}
      </div>
    `
  }

  renderToggleField(field, value) {
    const checked = value === true || value === "1" || value === "true"
    return `
      ${this.renderLabel(field)}
      <div class="flex items-center mt-2">
        <button type="button" role="switch" aria-checked="${checked}"
                class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${checked ? 'bg-blue-600' : 'bg-gray-200'}"
                data-field-input data-toggle-btn>
          <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}"></span>
        </button>
      </div>
    `
  }

  renderDateField(field, value) {
    return `
      ${this.renderLabel(field)}
      <input type="date" class="form-control" value="${value}" ${field.required ? 'required' : ''} data-field-input>
    `
  }

  renderTimeField(field, value) {
    return `
      ${this.renderLabel(field)}
      <input type="time" class="form-control" value="${value}" ${field.required ? 'required' : ''} data-field-input>
    `
  }

  renderDateTimeField(field, value) {
    return `
      ${this.renderLabel(field)}
      <input type="datetime-local" class="form-control" value="${value}" ${field.required ? 'required' : ''} data-field-input>
    `
  }

  renderRatingField(field, value) {
    const maxValue = field.options?.max_value || 5
    const currentValue = parseInt(value) || 0
    return `
      ${this.renderLabel(field)}
      <div class="flex items-center gap-1 mt-2" data-rating-container data-max="${maxValue}">
        <input type="hidden" value="${currentValue}" data-field-input data-rating-input>
        ${Array.from({length: maxValue}, (_, i) => `
          <button type="button" data-rating-star data-value="${i + 1}" class="focus:outline-hidden">
            <svg class="h-8 w-8 transition-colors duration-150 ${i < currentValue ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 cursor-pointer" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        `).join('')}
        <span class="ml-2 text-sm text-gray-500">${currentValue > 0 ? `${currentValue} / ${maxValue}` : 'Not rated'}</span>
      </div>
    `
  }

  renderPhotoField(field, value) {
    const signedIds = Array.isArray(value) ? value : (value ? [value] : [])
    const v = field.validations || {}
    const minFiles = v.min_files ? parseInt(v.min_files) : null
    const maxFiles = v.max_files ? parseInt(v.max_files) : null
    const maxFileSizeMB = 10
    const currentCount = signedIds.length
    const atMax = maxFiles !== null && currentCount >= maxFiles

    let hintParts = []
    if (minFiles !== null && maxFiles !== null) {
      hintParts.push(`${minFiles}–${maxFiles} photos required`)
    } else if (minFiles !== null) {
      hintParts.push(`At least ${minFiles} photo${minFiles !== 1 ? 's' : ''} required`)
    } else if (maxFiles !== null) {
      hintParts.push(`Up to ${maxFiles} photo${maxFiles !== 1 ? 's' : ''}`)
    }
    hintParts.push(`Max ${maxFileSizeMB}MB per file`)
    const hintHtml = `<p class="mt-1.5 text-xs text-gray-500">${hintParts.join(' · ')}</p>`

    const btnDisabledClass = atMax ? ' opacity-40 pointer-events-none' : ''

    const previewsHtml = signedIds.length > 0
      ? `<div class="mt-3 flex flex-wrap gap-3" data-photo-previews>
           ${signedIds.map((signedId, i) => {
             const photo = this.photoMap[signedId]
             if (photo && photo.url) {
               return `
                 <div class="relative group" data-photo-item data-signed-id="${this.escapeHtml(signedId)}">
                   <img src="${this.escapeHtml(photo.url)}"
                        class="h-24 w-24 object-cover rounded-lg border border-gray-200 bg-gray-50">
                   <button type="button"
                           data-remove-photo="${i}"
                           data-photo-id="${photo.id || ''}"
                           data-photo-signed-id="${this.escapeHtml(signedId)}"
                           data-photo-field-key="${this.escapeHtml(field.field_key)}"
                           class="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow opacity-0 group-hover:opacity-100 transition-opacity">
                     &times;
                   </button>
                 </div>`
             } else {
               return `
                 <div class="relative group" data-photo-item data-signed-id="${this.escapeHtml(signedId)}">
                   <div class="h-24 w-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-xs text-gray-500">
                     Photo ${i + 1}
                   </div>
                   <button type="button"
                           data-remove-photo="${i}"
                           data-photo-id=""
                           data-photo-signed-id="${this.escapeHtml(signedId)}"
                           data-photo-field-key="${this.escapeHtml(field.field_key)}"
                           class="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow opacity-0 group-hover:opacity-100 transition-opacity">
                     &times;
                   </button>
                 </div>`
             }
           }).join('')}
         </div>`
      : ''

    return `
      ${this.renderLabel(field)}
      <div class="mt-2" data-photo-field-container>
        <div class="flex items-center gap-2${btnDisabledClass}">
          <label class="inline-flex items-center gap-1.5 py-1.5 px-4 rounded-full text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer whitespace-nowrap">
            Choose files
            <input type="file" accept="image/*" multiple class="hidden" data-field-input data-photo-input ${atMax ? 'disabled' : ''}>
          </label>
          <label class="inline-flex items-center gap-1.5 py-1.5 px-4 rounded-full text-sm font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 cursor-pointer whitespace-nowrap">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
            </svg>
            Take Photo
            <input type="file" accept="image/*" capture="environment" class="hidden" data-field-input data-photo-input ${atMax ? 'disabled' : ''}>
          </label>
        </div>
        ${hintHtml}
        <div class="mt-1" data-upload-progress></div>
        ${previewsHtml}
      </div>
    `
  }

  renderSignatureField(field, value) {
    return `
      ${this.renderLabel(field)}
      <div class="mt-2 relative">
        <input type="hidden" value="${value || ''}" data-field-input data-signature-input>
        <div class="border-2 border-gray-300 rounded-lg bg-white">
          <canvas class="w-full h-40 cursor-crosshair rounded-lg" data-signature-canvas width="600" height="160"></canvas>
          <button type="button" data-signature-clear class="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="mt-1 text-xs text-gray-500">Draw your signature above. Click the X to clear.</p>
      </div>
    `
  }

  renderProductSelectField(field, value, instanceIndex) {
    const productsData = field.options?.products_data || []
    const selectionMode = field.options?.selection_mode || "multiple"
    const isMultiple = selectionMode === "multiple"
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : [])
    const name = `${field.field_key}_${instanceIndex !== null ? instanceIndex : 'single'}`

    if (productsData.length === 0) {
      return `
        ${this.renderLabel(field)}
        <p class="text-sm text-gray-500 mt-2 italic">No products available for selection.</p>
      `
    }

    const groupsHtml = productsData.map(group => {
      const productsHtml = (group.products || []).map(product => {
        const inputType = isMultiple ? "checkbox" : "radio"
        const checked = selectedValues.includes(product.id) ? "checked" : ""
        const dataAttr = isMultiple ? "data-product-checkbox-group" : "data-product-radio-group"
        const inputClass = isMultiple
          ? "h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          : "h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
        const sku = product.sku ? `<span class="text-xs text-gray-400 ml-1">(${this.escapeHtml(product.sku)})</span>` : ""
        return `
          <div class="flex items-center py-1">
            <input type="${inputType}" ${inputType === "radio" ? `name="${name}"` : ""}
                   value="${this.escapeHtml(product.id)}" ${checked}
                   class="${inputClass}" data-field-input ${dataAttr}>
            <label class="ml-3 text-sm text-gray-700">${this.escapeHtml(product.name)}${sku}</label>
          </div>
        `
      }).join("")

      return `
        <div class="mb-3">
          <div class="bg-gray-50 rounded-md px-3 py-1.5 mb-1">
            <span class="text-xs font-semibold text-gray-600 uppercase tracking-wider">${this.escapeHtml(group.group_name)}</span>
          </div>
          <div class="ml-3 space-y-0.5">${productsHtml}</div>
        </div>
      `
    }).join("")

    return `
      ${this.renderLabel(field)}
      <div class="mt-2 border border-gray-200 rounded-lg p-3 max-h-72 overflow-y-auto" data-product-select-container>
        ${groupsHtml}
      </div>
    `
  }

  renderProductInstallerField(field, value, sectionKey, instanceIndex) {
    const options = field.options || {}
    const ptDataList = options.product_type_data_list || []
    const addQtag = options.add_qtag === true || options.add_qtag === "true"
    const currentData = (typeof value === "object" && value !== null) ? value : {}
    const selectedProductTypeId = currentData.product_type_id || ""

    if (ptDataList.length === 0) {
      return `
        ${this.renderLabel(field)}
        <p class="text-sm text-gray-500 mt-2 italic">No product types available. Configure this field to restrict the product scope.</p>
      `
    }

    const productTypeOptions = ptDataList.map(pt => {
      const meta = [pt.sku ? `SKU: ${this.escapeHtml(pt.sku)}` : null, pt.manufacturer_name ? this.escapeHtml(pt.manufacturer_name) : null].filter(Boolean).join(" · ")
      const label = meta ? `${this.escapeHtml(pt.name)} — ${meta}` : this.escapeHtml(pt.name)
      return `<option value="${this.escapeHtml(pt.id)}" ${selectedProductTypeId === pt.id ? "selected" : ""}>${label}</option>`
    }).join("")

    const selectedPt = ptDataList.find(pt => pt.id === selectedProductTypeId) || null
    const attributesHtml = selectedPt ? this.renderProductInstallerAttributes(selectedPt, currentData.attributes || {}) : ""

    const qtagHtml = addQtag ? this.renderProductInstallerQtag(currentData.qtag || "") : ""

    return `
      ${this.renderLabel(field)}
      <div class="mt-2" data-product-installer-container>
        <div class="mb-3">
          <label class="label">Product Type<span class="text-red-500 ml-1">*</span></label>
          <select class="form-control" data-pi-product-type>
            <option value="">— Select a product type —</option>
            ${productTypeOptions}
          </select>
        </div>
        <div data-pi-attributes class="space-y-1">
          ${attributesHtml}
        </div>
        ${qtagHtml}
      </div>
    `
  }

  renderProductInstallerAttributes(ptData, attributes) {
    return (ptData.attribute_fields || []).map(af => {
      const attrVal = attributes[af.field_key] !== undefined ? attributes[af.field_key] : ""
      const reqMark = af.required ? '<span class="text-red-500 ml-1">*</span>' : ''
      const validations = af.validations || {}

      switch (af.field_type) {
        case 'number':
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <input type="number" class="form-control" value="${this.escapeHtml(String(attrVal))}"
                   placeholder="${this.escapeHtml(af.label)}"
                   ${validations.min !== undefined ? `min="${validations.min}"` : ''}
                   ${validations.max !== undefined ? `max="${validations.max}"` : ''}
                   ${af.required ? 'required' : ''} data-pi-attr="${this.escapeHtml(af.field_key)}"></div>`
        case 'textarea':
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <textarea class="form-control" rows="3" placeholder="${this.escapeHtml(af.label)}"
                      ${af.required ? 'required' : ''} data-pi-attr="${this.escapeHtml(af.field_key)}">${this.escapeHtml(String(attrVal))}</textarea></div>`
        case 'select': {
          const selectOpts = (af.options?.options || [])
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <select class="form-control" ${af.required ? 'required' : ''} data-pi-attr="${this.escapeHtml(af.field_key)}">
              <option value="">Select...</option>
              ${selectOpts.map(o => `<option value="${this.escapeHtml(o.value)}" ${attrVal === o.value ? 'selected' : ''}>${this.escapeHtml(o.label)}</option>`).join('')}
            </select></div>`
        }
        case 'toggle': {
          const toggled = attrVal === true || attrVal === 'true' || attrVal === '1'
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <div class="flex items-center gap-2 mt-1">
              <input type="checkbox" ${toggled ? 'checked' : ''}
                     class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                     data-pi-attr="${this.escapeHtml(af.field_key)}" data-pi-toggle>
              <span class="text-sm text-gray-600">${toggled ? 'Yes' : 'No'}</span>
            </div></div>`
        }
        case 'date':
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <input type="date" class="form-control" value="${this.escapeHtml(String(attrVal))}"
                   ${af.required ? 'required' : ''} data-pi-attr="${this.escapeHtml(af.field_key)}"></div>`
        default:
          return `<div class="mb-3"><label class="label">${this.escapeHtml(af.label)}${reqMark}</label>
            <input type="text" class="form-control" value="${this.escapeHtml(String(attrVal))}"
                   placeholder="${this.escapeHtml(af.label)}" ${af.required ? 'required' : ''}
                   data-pi-attr="${this.escapeHtml(af.field_key)}"></div>`
      }
    }).join('')
  }

  renderProductInstallerQtag(qtagVal) {
    return renderQrScannerField({
      value: qtagVal,
      required: true,
      label: "Qtag Reference",
      wrapperClass: "mb-3 mt-4 pt-4 border-t border-gray-200",
      inputAttr: "data-pi-qtag",
    })
  }

  renderQtagScanField(field, value) {
    return `
      ${this.renderLabel(field)}
      ${renderQrScannerField({ value, inputAttr: "data-field-input" })}
    `
  }

  renderSectionBreak(field) {
    return `
      <div class="border-t border-gray-200 my-6 pt-4">
        <h4 class="text-sm font-medium text-gray-500">${field.label}</h4>
      </div>
    `
  }

  // =========================================================================
  // Field event listeners
  // =========================================================================

  attachFieldListeners(wrapper, field, sectionKey, instanceIndex) {
    const clearWrapperError = () => {
      wrapper.querySelectorAll('.form-control.error').forEach(el => el.classList.remove('error'))
      wrapper.querySelectorAll('[data-widget-error-outline]').forEach(el => {
        el.classList.remove('border', 'border-red-400', 'rounded-md', 'p-3')
        delete el.dataset.widgetErrorOutline
      })
      const errEl = wrapper.querySelector('[data-field-error]')
      if (errEl) errEl.remove()
    }

    const inputs = wrapper.querySelectorAll('[data-field-input]:not([data-checkbox-group]):not([data-toggle-btn]):not([data-rating-input]):not([data-signature-input]):not([data-photo-input]):not([data-product-checkbox-group]):not([data-product-radio-group])')
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        this.setFieldValue(field.field_key, input.value, sectionKey, instanceIndex)
        clearWrapperError()
        this.renderSectionStep()
      })
      input.addEventListener('input', () => {
        this.setFieldValue(field.field_key, input.value, sectionKey, instanceIndex)
        clearWrapperError()
      })
    })

    const checkboxes = wrapper.querySelectorAll('[data-checkbox-group]')
    if (checkboxes.length > 0) {
      checkboxes.forEach(cb => {
        cb.addEventListener('change', () => {
          const values = Array.from(wrapper.querySelectorAll('[data-checkbox-group]:checked')).map(c => c.value)
          this.setFieldValue(field.field_key, values, sectionKey, instanceIndex)
        })
      })
    }

    const productCheckboxes = wrapper.querySelectorAll('[data-product-checkbox-group]')
    if (productCheckboxes.length > 0) {
      productCheckboxes.forEach(cb => {
        cb.addEventListener('change', () => {
          const values = Array.from(wrapper.querySelectorAll('[data-product-checkbox-group]:checked')).map(c => c.value)
          this.setFieldValue(field.field_key, values, sectionKey, instanceIndex)
        })
      })
    }

    const productRadios = wrapper.querySelectorAll('[data-product-radio-group]')
    if (productRadios.length > 0) {
      productRadios.forEach(radio => {
        radio.addEventListener('change', () => {
          this.setFieldValue(field.field_key, radio.value, sectionKey, instanceIndex)
        })
      })
    }

    const toggleBtn = wrapper.querySelector('[data-toggle-btn]')
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isChecked = toggleBtn.getAttribute('aria-checked') === 'true'
        const newValue = !isChecked
        toggleBtn.setAttribute('aria-checked', newValue)
        toggleBtn.classList.toggle('bg-blue-600', newValue)
        toggleBtn.classList.toggle('bg-gray-200', !newValue)
        toggleBtn.querySelector('span').classList.toggle('translate-x-5', newValue)
        toggleBtn.querySelector('span').classList.toggle('translate-x-0', !newValue)
        this.setFieldValue(field.field_key, newValue, sectionKey, instanceIndex)
      })
    }

    const ratingContainer = wrapper.querySelector('[data-rating-container]')
    if (ratingContainer) {
      const stars = ratingContainer.querySelectorAll('[data-rating-star]')
      const ratingInput = ratingContainer.querySelector('[data-rating-input]')
      stars.forEach(star => {
        star.addEventListener('click', () => {
          const val = parseInt(star.dataset.value)
          const current = parseInt(ratingInput.value) || 0
          const newVal = val === current ? 0 : val
          ratingInput.value = newVal
          this.setFieldValue(field.field_key, newVal, sectionKey, instanceIndex)
          stars.forEach((s, i) => {
            const svg = s.querySelector('svg')
            svg.classList.toggle('text-yellow-400', i < newVal)
            svg.classList.toggle('text-gray-300', i >= newVal)
          })
          const display = ratingContainer.querySelector('.text-gray-500')
          if (display) display.textContent = newVal > 0 ? `${newVal} / ${ratingContainer.dataset.max}` : 'Not rated'
        })
      })
    }

    const signatureCanvas = wrapper.querySelector('[data-signature-canvas]')
    if (signatureCanvas) {
      this.initSignatureCanvas(signatureCanvas, field, sectionKey, instanceIndex, wrapper)
    }

    const photoInputs = wrapper.querySelectorAll('[data-photo-input]')
    if (photoInputs.length > 0) {
      photoInputs.forEach(photoInput => {
        photoInput.addEventListener('change', (e) => {
          this.handlePhotoUpload(e, field, sectionKey, instanceIndex, wrapper)
        })
      })

      wrapper.querySelectorAll('[data-remove-photo]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const photoId   = btn.dataset.photoId
          const signedId  = btn.dataset.photoSignedId
          const fieldKey  = btn.dataset.photoFieldKey

          if (photoId && !this.previewValue) {
            try {
              const params = new URLSearchParams({
                signed_id: signedId,
                field_key: fieldKey
              })
              if (sectionKey) params.set('section_key', sectionKey)
              if (instanceIndex !== null && instanceIndex !== undefined) {
                params.set('instance_index', String(instanceIndex))
              }
              const urlBase = this.deletePhotoUrlBaseValue || `/submissions/${this.submissionIdValue}`
              await fetch(`${urlBase}/photos/${photoId}?${params.toString()}`, {
                method: 'DELETE',
                headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' }
              })
            } catch (e) {
              console.error('Failed to delete photo:', e)
            }
          }

          delete this.photoMap[signedId]

          const current = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
          const arr = Array.isArray(current) ? current.filter(id => id !== signedId) : []
          this.setFieldValue(field.field_key, arr.length > 0 ? arr : "", sectionKey, instanceIndex)
          this.renderSectionStep()
        })
      })
    }

    const piContainer = wrapper.querySelector('[data-product-installer-container]')
    if (piContainer) {
      const ptDataList = (field.options || {}).product_type_data_list || []

      const buildValue = () => {
        const current = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
        const data = (typeof current === 'object' && current !== null) ? { ...current } : {}

        const ptSelect = piContainer.querySelector('[data-pi-product-type]')
        data.product_type_id = ptSelect ? ptSelect.value : ""

        const attrs = {}
        piContainer.querySelectorAll('[data-pi-attr]').forEach(input => {
          attrs[input.dataset.piAttr] = input.dataset.piToggle !== undefined ? input.checked : input.value
        })
        data.attributes = attrs

        const qtagInput = piContainer.querySelector('[data-pi-qtag]')
        if (qtagInput) data.qtag = qtagInput.value

        return data
      }

      const ptSelect = piContainer.querySelector('[data-pi-product-type]')
      if (ptSelect) {
        ptSelect.addEventListener('change', () => {
          const selectedId = ptSelect.value
          const selectedPt = ptDataList.find(pt => pt.id === selectedId) || null
          const attrContainer = piContainer.querySelector('[data-pi-attributes]')
          if (attrContainer) {
            attrContainer.innerHTML = selectedPt ? this.renderProductInstallerAttributes(selectedPt, {}) : ""
            attrContainer.querySelectorAll('input, textarea, select').forEach(el => {
              el.addEventListener('input', () => this.setFieldValue(field.field_key, buildValue(), sectionKey, instanceIndex))
              el.addEventListener('change', () => this.setFieldValue(field.field_key, buildValue(), sectionKey, instanceIndex))
            })
          }
          this.setFieldValue(field.field_key, buildValue(), sectionKey, instanceIndex)
        })
      }

      piContainer.querySelectorAll('input, textarea, select').forEach(input => {
        input.addEventListener('input', () => this.setFieldValue(field.field_key, buildValue(), sectionKey, instanceIndex))
        input.addEventListener('change', () => this.setFieldValue(field.field_key, buildValue(), sectionKey, instanceIndex))
      })
    }

  }

  // =========================================================================
  // Direct Upload for photos
  // =========================================================================

  handlePhotoUpload(event, field, sectionKey, instanceIndex, wrapper) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    const progressContainer = wrapper.querySelector('[data-upload-progress]')
    const v = field.validations || {}
    const maxFiles = v.max_files ? parseInt(v.max_files) : null
    const maxFileSizeBytes = 10 * 1024 * 1024 // 10MB system limit

    // Reject oversized files immediately
    const oversized = files.filter(f => f.size > maxFileSizeBytes)
    let filesToUpload = files.filter(f => f.size <= maxFileSizeBytes)

    oversized.forEach(f => {
      const el = document.createElement('div')
      el.className = 'text-red-500 text-xs mt-1'
      el.textContent = `"${f.name}" is too large (max 10MB)`
      if (progressContainer) progressContainer.appendChild(el)
      setTimeout(() => el.remove(), 5000)
    })

    // Enforce max file count
    if (maxFiles !== null) {
      const current = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
      const currentCount = Array.isArray(current) ? current.length : (current ? 1 : 0)
      const remaining = maxFiles - currentCount

      if (remaining <= 0) {
        const el = document.createElement('div')
        el.className = 'text-orange-600 text-xs mt-1'
        el.textContent = `Maximum of ${maxFiles} photo${maxFiles !== 1 ? 's' : ''} already reached`
        if (progressContainer) progressContainer.appendChild(el)
        setTimeout(() => el.remove(), 4000)
        return
      }

      if (filesToUpload.length > remaining) {
        const skipped = filesToUpload.length - remaining
        filesToUpload = filesToUpload.slice(0, remaining)
        const el = document.createElement('div')
        el.className = 'text-orange-600 text-xs mt-1'
        el.textContent = `${skipped} file${skipped !== 1 ? 's' : ''} skipped — maximum of ${maxFiles} photos allowed`
        if (progressContainer) progressContainer.appendChild(el)
        setTimeout(() => el.remove(), 5000)
      }
    }

    if (filesToUpload.length === 0) return

    if (this.previewValue) return this.previewPhotos(filesToUpload, field, sectionKey, instanceIndex)

    filesToUpload.forEach(file => {
      const progressEl = document.createElement('div')
      progressEl.className = 'flex items-center gap-2 text-sm text-gray-600 mt-1'
      progressEl.innerHTML = `
        <div class="flex-1 bg-gray-200 rounded-full h-2">
          <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%" data-progress-bar></div>
        </div>
        <span data-progress-text>0%</span>
      `
      if (progressContainer) progressContainer.appendChild(progressEl)

      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR(request) {
          request.upload.addEventListener("progress", event => {
            const percent = Math.round((event.loaded / event.total) * 100)
            const bar = progressEl.querySelector('[data-progress-bar]')
            const text = progressEl.querySelector('[data-progress-text]')
            if (bar) bar.style.width = `${percent}%`
            if (text) text.textContent = `${percent}%`
          })
        }
      })

      const localUrl = URL.createObjectURL(file)

      upload.create((error, blob) => {
        if (error) {
          console.error("Upload error:", error)
          progressEl.innerHTML = `<span class="text-red-500 text-xs">Upload failed</span>`
          URL.revokeObjectURL(localUrl)
          return
        }

        progressEl.innerHTML = `<span class="text-green-600 text-xs">Uploaded</span>`
        setTimeout(() => progressEl.remove(), 2000)

        // Store local preview URL so the image renders immediately
        this.photoMap[blob.signed_id] = { id: null, url: localUrl }

        const current = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
        const arr = Array.isArray(current) ? [...current] : (current ? [current] : [])
        arr.push(blob.signed_id)
        this.setFieldValue(field.field_key, arr, sectionKey, instanceIndex)
        this.renderSectionStep()
      })
    })
  }

  // Preview: no direct upload (that would create blobs). The photo shows
  // from a local object URL under a placeholder id the server never sees.
  previewPhotos(files, field, sectionKey, instanceIndex) {
    const current = this.getFieldValue(field.field_key, sectionKey, instanceIndex)
    const arr = Array.isArray(current) ? [...current] : (current ? [current] : [])
    files.forEach(file => {
      this.previewPhotoSeq = (this.previewPhotoSeq || 0) + 1
      const placeholderId = `preview-photo-${this.previewPhotoSeq}`
      this.photoMap[placeholderId] = { id: null, url: URL.createObjectURL(file) }
      arr.push(placeholderId)
    })
    this.setFieldValue(field.field_key, arr, sectionKey, instanceIndex)
    this.renderSectionStep()
  }

  // =========================================================================
  // Signature canvas
  // =========================================================================

  initSignatureCanvas(canvas, field, sectionKey, instanceIndex, wrapper) {
    const ctx = canvas.getContext('2d')
    let isDrawing = false
    let lastX = 0
    let lastY = 0

    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const input = wrapper.querySelector('[data-signature-input]')

    if (input.value) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0)
      img.src = input.value
    }

    const getCoords = (e) => {
      const rect = canvas.getBoundingClientRect()
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
      }
    }

    canvas.addEventListener('mousedown', (e) => {
      isDrawing = true
      const c = getCoords(e); lastX = c.x; lastY = c.y
    })
    canvas.addEventListener('mousemove', (e) => {
      if (!isDrawing) return
      const c = getCoords(e)
      ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineTo(c.x, c.y); ctx.stroke()
      lastX = c.x; lastY = c.y
    })
    canvas.addEventListener('mouseup', () => {
      if (isDrawing) {
        isDrawing = false
        input.value = canvas.toDataURL('image/png')
        this.setFieldValue(field.field_key, input.value, sectionKey, instanceIndex)
      }
    })
    canvas.addEventListener('mouseout', () => {
      if (isDrawing) {
        isDrawing = false
        input.value = canvas.toDataURL('image/png')
        this.setFieldValue(field.field_key, input.value, sectionKey, instanceIndex)
      }
    })

    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault()
      const t = e.touches[0]
      canvas.dispatchEvent(new MouseEvent('mousedown', { clientX: t.clientX, clientY: t.clientY }))
    })
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault()
      const t = e.touches[0]
      canvas.dispatchEvent(new MouseEvent('mousemove', { clientX: t.clientX, clientY: t.clientY }))
    })
    canvas.addEventListener('touchend', () => canvas.dispatchEvent(new MouseEvent('mouseup')))

    const clearBtn = wrapper.querySelector('[data-signature-clear]')
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        input.value = ''
        this.setFieldValue(field.field_key, '', sectionKey, instanceIndex)
      })
    }
  }

  // =========================================================================
  // Validation
  // =========================================================================

  fieldErrorKey(sectionKey, instanceIndex, fieldKey) {
    return `${sectionKey}|${instanceIndex === null || instanceIndex === undefined ? "_" : instanceIndex}|${fieldKey}`
  }

  clearFieldError(sectionKey, instanceIndex, fieldKey) {
    delete this.fieldErrors[this.fieldErrorKey(sectionKey, instanceIndex, fieldKey)]
  }

  // Next never blocks on a missing required answer (decision-32 amended by
  // TASK-257) — only on an invalid VALUE (format/range), and even those stay
  // quiet pre-attempt beyond the block itself. Once submitAttempted, every
  // problem decorates and blocks nothing extra (Submit is the only gate from
  // then on; Next still lets a visitor move freely to go fix things).
  validateCurrentStep({ silent = false } = {}) {
    if (this.isAssetSelectStep) {
      if (this.assetSelectInvalidProblem()) return this.validateAssetSelectStep()
      return true
    }

    if (this.isSiteSelectStep) {
      if (this.siteSelectInvalidProblem()) return this.validateSiteSelectStep()
      return true
    }

    const section = this.currentSection
    if (!section) return true

    const problems = this.collectSectionErrors(section)
    const blocking = problems.filter(p => p.kind === "invalid")
    // Pre-attempt only invalid values decorate; once an attempt has run,
    // blanks decorate too (board 06) — neither ever blocks beyond "blocking".
    const decorate = this.submitAttempted ? problems : blocking

    this.fieldErrors = {}
    decorate.forEach(({ instanceIndex, fieldKey, message }) => {
      const k = this.fieldErrorKey(section.section_key, instanceIndex, fieldKey)
      if (!this.fieldErrors[k]) this.fieldErrors[k] = message
    })

    if (blocking.length > 0) {
      this.renderSectionStep()
      if (!silent) this.dispatchToast("Please check required inputs", "error")
      return false
    }

    if (decorate.length > 0) this.renderSectionStep()
    this.hideError()
    return true
  }

  // Pure collection of one section's problems — shared by step validation
  // (inline fieldErrors), the summary hub (issue cards + badges) and Submit
  // gating (readyToSubmit). Mirrors InspectionValidator's rules and never
  // mutates controller state.
  collectSectionErrors(section) {
    const found = []
    const seen = new Set()
    const instanceIndices = section.repeatable
      ? Array.from({ length: this.instanceCounts[section.section_key] || 1 }, (_, i) => i)
      : [null]

    // kind splits every problem into "blank" (a required answer is missing —
    // Next must let this through, decision-32 amended by TASK-257) and
    // "invalid" (a format/range problem on a value that IS present — Next
    // still blocks on these, and they show inline before any Submit attempt).
    const setError = (instanceIndex, fieldKey, message, kind = "invalid") => {
      const k = this.fieldErrorKey(section.section_key, instanceIndex, fieldKey)
      if (seen.has(k)) return
      seen.add(k)
      const field = section.fields.find(f => f.field_key === fieldKey)
      found.push({ instanceIndex, fieldKey, label: field?.label || fieldKey, message, kind })
    }

    instanceIndices.forEach(instanceIndex => {
      section.fields.forEach(field => {
        // A section break is a heading, not a question (parity with the
        // server: required on one is inert — TASK-164.2).
        if (field.field_type === "section_break") return
        if (!this.isFieldVisible(field, section.section_key, instanceIndex)) return

        const value = this.getFieldValue(field.field_key, section.section_key, instanceIndex)

        if (field.required) {
          if (value === "" || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
            setError(instanceIndex, field.field_key, `${field.label} is required`, "blank")
          }
        }

        if (field.validations && value !== "" && value !== null) {
          const v = field.validations
          if (v.min !== undefined && parseFloat(value) < v.min) setError(instanceIndex, field.field_key, `Must be at least ${v.min}`)
          if (v.max !== undefined && parseFloat(value) > v.max) setError(instanceIndex, field.field_key, `Must be at most ${v.max}`)
          if (v.min_length && String(value).length < v.min_length) setError(instanceIndex, field.field_key, `Must be at least ${v.min_length} characters`)
          if (v.max_length && String(value).length > v.max_length) setError(instanceIndex, field.field_key, `Must be at most ${v.max_length} characters`)
          if (v.pattern && !new RegExp(v.pattern).test(value)) setError(instanceIndex, field.field_key, `Format is invalid`)

          if (field.field_type === "product_select" || field.field_type === "checkbox") {
            const selections = Array.isArray(value) ? value : (value ? [value] : [])
            if (v.min_selections && selections.length < parseInt(v.min_selections)) setError(instanceIndex, field.field_key, `Requires at least ${v.min_selections} selection(s)`, "blank")
            if (v.max_selections && selections.length > parseInt(v.max_selections)) setError(instanceIndex, field.field_key, `Allows at most ${v.max_selections} selection(s)`)
          }

          if (field.field_type === "photo") {
            const photos = Array.isArray(value) ? value : (value ? [value] : [])
            if (v.min_files && photos.length < parseInt(v.min_files)) {
              const n = parseInt(v.min_files)
              setError(instanceIndex, field.field_key, `Requires at least ${n} photo${n !== 1 ? 's' : ''}`, "blank")
            }
            if (v.max_files && photos.length > parseInt(v.max_files)) {
              const n = parseInt(v.max_files)
              setError(instanceIndex, field.field_key, `Must have at most ${n} photo${n !== 1 ? 's' : ''}`)
            }
          }
        }

        if (field.field_type === "product_installer") {
          const piOpts = field.options || {}
          const piData = (typeof value === 'object' && value !== null) ? value : {}
          const ptDataList = piOpts.product_type_data_list || []

          if (!piData.product_type_id || piData.product_type_id.trim() === '') {
            setError(instanceIndex, field.field_key, `A product type must be selected`, "blank")
          } else {
            const selectedPt = ptDataList.find(pt => pt.id === piData.product_type_id) || null
            if (selectedPt?.attribute_fields) {
              const missing = selectedPt.attribute_fields.filter(af => {
                if (!af.required) return false
                const attrVal = (piData.attributes || {})[af.field_key]
                return attrVal === '' || attrVal === null || attrVal === undefined
              })
              if (missing.length > 0) {
                setError(instanceIndex, field.field_key, `${missing.map(af => af.label).join(", ")} ${missing.length === 1 ? "is" : "are"} required`, "blank")
              }
            }
          }

          if (piOpts.add_qtag === true || piOpts.add_qtag === 'true') {
            if (!piData.qtag || piData.qtag.toString().trim() === '') {
              setError(instanceIndex, field.field_key, `QTAG Reference is required`, "blank")
            } else if (!/^[A-Z]\d{6}$/.test(piData.qtag.toString().trim())) {
              setError(instanceIndex, field.field_key, `QTAG Reference must be a letter followed by 6 digits (e.g. A000001)`)
            }
          }

        }
      })
    })

    return found
  }

  validateSiteSelectStep() {
    this.siteFieldErrors = {}
    const setErr = (key, msg) => { if (!this.siteFieldErrors[key]) this.siteFieldErrors[key] = msg }

    // Customer reference is a spine field sharing this step's screen, not
    // one of siteSelectionProblems' "Site & location" rules — checked here
    // directly so it still blocks Next/decorates this step, without leaking
    // into the rail badge (siteSelectionSatisfied / siteSelectBlankOnly).
    const refCfg = this.schemaValue.header?.customer_reference || {}
    if (refCfg.enabled && refCfg.required && !(this.customerReference || '').trim()) {
      setErr('customer_reference', `${refCfg.label || 'Your Ref'} is required`)
    }

    this.siteSelectionProblems().forEach(problem => setErr(problem.key, problem.message))

    if (Object.keys(this.siteFieldErrors).length > 0) {
      this.renderSectionStep()
      this.dispatchToast("Please check required inputs", "error")
      return false
    }
    this.hideError()
    return true
  }

  showError(errors) {
    this.errorListTarget.innerHTML = errors.map(e => `<li>${e}</li>`).join('')
    this.errorContainerTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorContainerTarget.classList.add("hidden")
  }

  // =========================================================================
  // Navigation actions
  // =========================================================================

  updateNavigation() {
    // The footer contract (decision-32): the hub carries Cancel + the only
    // Submit; steps carry Back + Next. Back on the first step, and Next on
    // the last, both land on the hub. Submit is always enabled (TASK-257) —
    // a click with gaps sets submitAttempted and re-renders Review instead
    // of being disabled outright.
    if (this.hasPreviewNoteTarget) this.previewNoteTarget.classList.toggle("hidden", !this.onSummary)
    if (this.onSummary) {
      // Preview (board 06): Back + "Nothing is sent from a preview" + Finish
      // Preview — there is no draft to leave, so no Cancel.
      this.prevBtnTarget.classList.toggle("hidden", !this.previewValue)
      this.nextBtnTarget.classList.add("hidden")
      this.saveDraftBtnTarget.classList.add("hidden")
      if (this.hasStepRemainingLabelTarget) this.stepRemainingLabelTarget.classList.add("hidden")
      if (this.hasCancelLinkTarget) this.cancelLinkTarget.classList.toggle("hidden", this.previewValue)
      this.submitBtnTarget.classList.remove("hidden")
      this.submitBtnTarget.disabled = false
      return
    }

    // The first step leads the walk (TASK-257, board 02): there is nothing
    // before it, so its left-hand button is Cancel (leave the draft), not a
    // Back that would jump ahead to Review & Submit.
    const firstStep = this.currentStep === 0
    if (this.hasCancelLinkTarget) this.cancelLinkTarget.classList.toggle("hidden", !firstStep)
    this.prevBtnTarget.classList.toggle("hidden", firstStep)
    this.nextBtnTarget.classList.remove("hidden")
    this.submitBtnTarget.classList.add("hidden")
    this.saveDraftBtnTarget.classList.add("hidden")

    // Every Next button names its destination (point 3) — the last
    // section's Next names Review & Submit.
    if (this.hasNextBtnLabelTarget) {
      const titles = this.stepTitles()
      const destination = this.isLastSectionStep ? "Review & Submit" : (titles[this.currentStep + 1] || "")
      this.nextBtnLabelTarget.textContent = destination ? `Next: ${destination}` : "Next"
    }

    if (this.hasStepRemainingLabelTarget) {
      const remaining = this.remainingCount(this.currentStep)
      if (remaining > 0) {
        this.stepRemainingLabelTarget.textContent = `${remaining} still to answer here`
        this.stepRemainingLabelTarget.classList.remove("hidden")
      } else {
        this.stepRemainingLabelTarget.classList.add("hidden")
      }
    }
  }

  previousStep() {
    // Back from Review & Submit (only offered in preview) returns to the last step.
    if (this.onSummary) return this.goToStep(this.totalSteps - 1)

    if (this.currentStep > 0) {
      this.currentStep--
      this.scheduleSave()
      this.renderCurrentStep()
    } else {
      this.goToSummary()
    }
  }

  nextStep() {
    if (!this.validateCurrentStep()) return

    this.completedSteps.add(this.currentStep)
    if (this.isLastSectionStep) {
      this.onSummary = true
    } else {
      this.currentStep++
    }

    this.saveToServer({ notify: true })
    this.renderCurrentStep()
  }

  addInstance() {
    const section = this.currentSection
    if (!section?.repeatable) return
    if (!this.validateCurrentStep()) return

    const sectionKey = section.section_key
    const currentCount = this.instanceCounts[sectionKey] || 1

    if (section.max_repetitions && currentCount >= section.max_repetitions) return

    this.instanceCounts[sectionKey] = currentCount + 1

    if (!this.formData[sectionKey]) this.formData[sectionKey] = []
    this.formData[sectionKey].push({ _index: currentCount + 1, _label: "" })

    this.scheduleSave()
    this.renderCurrentStep()
  }

  removeInstance(event) {
    const section = this.currentSection
    if (!section?.repeatable) return

    const sectionKey = section.section_key
    const currentCount = this.instanceCounts[sectionKey] || 1

    if (currentCount <= (section.min_repetitions || 1)) return

    const index = parseInt(event.currentTarget.dataset.instanceIndex)
    if (isNaN(index)) return

    if (this.formData[sectionKey]) {
      this.formData[sectionKey].splice(index, 1)
      this.formData[sectionKey].forEach((item, i) => { item._index = i + 1 })
    }

    this.instanceCounts[sectionKey] = currentCount - 1

    this.scheduleSave()
    this.renderCurrentStep()
  }

  // =========================================================================
  // Save & Submit
  // =========================================================================

  async saveDraft() {
    await this.saveToServer({ notify: true })
  }

  async submitForm() {
    if (this.previewValue) return this.finishPreview()

    // Submit lives on Review & Submit (decision-32) and is always enabled
    // (TASK-257) — a click with gaps arms submitAttempted and re-renders
    // Review in its attempted state (board 06) instead of being disabled or
    // toasting; nothing has been sent.
    if (!this.readyToSubmit()) {
      this.submitAttempted = true
      this.renderCurrentStep()
      return
    }

    this.submitBtnTarget.disabled = true

    try {
      await this.flushSave()

      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ data: this.formData })
      })

      const result = await response.json()

      if (response.ok && result.redirect_to) {
        window.location.href = result.redirect_to
      } else if (result.errors) {
        const errorMessages = Object.entries(result.errors).flatMap(([key, msgs]) =>
          msgs.map(msg => `${key}: ${msg}`)
        )
        this.showError(errorMessages)
        this.submitBtnTarget.disabled = false
      }
    } catch (e) {
      console.error("Submit failed:", e)
      this.showError(["An error occurred while submitting. Please try again."])
      this.submitBtnTarget.disabled = false
    }
  }

  // Preview's Finish Preview / Cancel: close the builder's preview modal.
  // Nothing to flush — a preview never saved anything.
  finishPreview() {
    window.dispatchEvent(new CustomEvent("modal:close-requested"))
  }

  // =========================================================================
  // Utility
  // =========================================================================

  escapeHtml(str) {
    if (!str) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
